import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { API_WEB_URLS } from "../../constants/constAPI";
import ExcelJS from "exceljs";
import useSession from "../utils/useSession";
import { filterActiveMasters } from "../utils/masterFilters";
import {
  MONTH_NAMES,
  getCalculatedMonthDays,
  getWeeklyHolidayCount,
  getEffectiveSalary,
  computeWorkingDays,
  computeBasic,
  recomputeRow,
} from "./PayrollHelpers";

// ─────────────────────────────────────────────────────────────
// Native browser download helper
// ─────────────────────────────────────────────────────────────
const saveAs = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 300);
};

// ─────────────────────────────────────────────────────────────
// Fields Definition for Payroll Excel Template & Export
// ─────────────────────────────────────────────────────────────
export const PAYROLL_FIELDS = [
  // 1. Employee Identification
  { key: "EmployeeCode", label: "Employee Code", required: true, example: "0001", width: 18, category: "Identification", hint: "Unique Code / Text" },
  { key: "EmployeeName", label: "Employee Name", required: false, example: "Rahul Sharma", width: 24, category: "Identification", hint: "Required Full Name" },
  { key: "FatherName", label: "Father Name", required: false, example: "Ramesh Sharma", width: 20, category: "Identification", hint: "Text" },
  { key: "DepartmentName", label: "Department", required: false, example: "Production", width: 18, category: "Identification", hint: "Master / Text" },
  { key: "DesignationName", label: "Designation", required: false, example: "Supervisor", width: 18, category: "Identification", hint: "Master / Text" },

  // 2. Attendance & Days
  { key: "BasicSalary", label: "Basic Salary", required: true, type: "number", example: "15000", width: 16, category: "Salary", hint: "Amount / Number (> 0)" },
  { key: "MonthDays", label: "Month Days", type: "number", example: "30", width: 14, category: "Attendance", hint: "Number: 28-31" },
  { key: "WorkingDays", label: "Working Days", type: "number", example: "26", width: 14, category: "Attendance", hint: "Number: 0-31" },
  { key: "PaidDay", label: "Paid Days", type: "number", example: "26", width: 14, category: "Attendance", hint: "Number: 0-31" },
  { key: "WorkingHours", label: "Working Hours", type: "number", example: "208", width: 14, category: "Attendance", hint: "Hours" },
  { key: "WorkingMinutes", label: "Working Mins", type: "number", example: "0", width: 14, category: "Attendance", hint: "Minutes" },
  { key: "OvertimeHours", label: "OT Hours", type: "number", example: "10", width: 14, category: "Attendance", hint: "Hours" },
  { key: "OverTime", label: "OT Amount", type: "number", example: "1200", width: 14, category: "Salary", hint: "Amount / Number" },
  { key: "LeaveWages", label: "Leave Wages", type: "number", example: "0", width: 14, category: "Salary", hint: "Amount / Number" },
  { key: "FestivalHoliday", label: "Festival Holiday", type: "number", example: "1", width: 16, category: "Attendance", hint: "Number / Days" },
  { key: "WeeklyHoliday", label: "Weekly Off", type: "number", example: "4", width: 14, category: "Attendance", hint: "Number / Days" },
  { key: "CL", label: "Casual Leave (CL)", type: "number", example: "0", width: 16, category: "Attendance", hint: "Number / Days" },
  { key: "ML", label: "Medical Leave (ML)", type: "number", example: "0", width: 16, category: "Attendance", hint: "Number / Days" },

  // 3. Allowances & Extra Earnings
  { key: "Arrear", label: "Arrear", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number" },
  { key: "AdvanceBonus", label: "Advance Bonus", type: "number", example: "0", width: 16, category: "Earnings", hint: "Amount / Number" },
  { key: "ExtraPay", label: "Extra Pay 1", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number" },
  { key: "ExtraPay1", label: "Extra Pay 2", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number" },
  { key: "Bonus", label: "Bonus", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number" },
  { key: "Gratuity", label: "Gratuity", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number" },
  { key: "HRA", label: "HRA", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number" },
  { key: "Conveyance", label: "Conveyance", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number" },
  { key: "CCA", label: "CCA", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number" },
  { key: "Medical", label: "Medical", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number" },
  { key: "Retention", label: "Retention", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number" },
  { key: "TotalAllowances", label: "Total Allowances", type: "number", example: "0", width: 16, category: "Earnings", hint: "Amount / Number" },
  { key: "Wages", label: "Earned Basic / Wages", type: "number", example: "15000", width: 22, category: "Earnings", hint: "Amount / Number" },
  { key: "GrossAmount", label: "Gross Amount", type: "number", example: "16200", width: 16, category: "Earnings", hint: "Amount / Number" },

  // 4. Statutory PF & Pension
  { key: "PF", label: "Employee PF", type: "number", example: "1800", width: 18, category: "Statutory", hint: "Amount / Number" },
  { key: "EmplrPF", label: "Employer PF", type: "number", example: "1800", width: 18, category: "Statutory", hint: "Amount / Number" },
  { key: "Pension", label: "Pension (EPS)", type: "number", example: "1250", width: 16, category: "Statutory", hint: "Amount / Number" },
  { key: "ArrearPF", label: "Arrear PF", type: "number", example: "0", width: 14, category: "Statutory", hint: "Amount / Number" },
  { key: "ArrERPF", label: "Arr ER PF", type: "number", example: "0", width: 14, category: "Statutory", hint: "Amount / Number" },
  { key: "ArrPens", label: "Arr Pension", type: "number", example: "0", width: 14, category: "Statutory", hint: "Amount / Number" },
  { key: "AdminCharges", label: "Admin Charges", type: "number", example: "0", width: 16, category: "Statutory", hint: "Amount / Number" },
  { key: "AC21", label: "AC 21 (EDLI)", type: "number", example: "0", width: 16, category: "Statutory", hint: "Amount / Number" },
  { key: "AC22", label: "AC 22 (EDLI Admin)", type: "number", example: "0", width: 18, category: "Statutory", hint: "Amount / Number" },

  // 5. Statutory ESI
  { key: "ESI", label: "Employee ESI", type: "number", example: "121.50", width: 18, category: "Statutory", hint: "Amount / Number" },
  { key: "EmplrESI", label: "Employer ESI", type: "number", example: "526.50", width: 18, category: "Statutory", hint: "Amount / Number" },

  // 6. Other Deductions & Net Pay
  { key: "TDS", label: "TDS", type: "number", example: "0", width: 12, category: "Deductions", hint: "Amount / Number" },
  { key: "Advance", label: "Advance", type: "number", example: "0", width: 14, category: "Deductions", hint: "Amount / Number" },
  { key: "Recovery", label: "Recovery", type: "number", example: "0", width: 14, category: "Deductions", hint: "Amount / Number" },
  { key: "PFRefund", label: "PF Refund", type: "number", example: "0", width: 14, category: "Deductions", hint: "Amount / Number" },
  { key: "TotalDeductions", label: "Total Other Deductions", type: "number", example: "0", width: 20, category: "Deductions", hint: "Amount / Number" },
  { key: "TotalDeduction", label: "Total Deductions", type: "number", example: "1921.50", width: 18, category: "Deductions", hint: "Amount / Number" },
  { key: "NetAmount", label: "Net Payable Amount", type: "number", example: "14278.50", width: 20, category: "Summary", hint: "Amount / Number" },

  // 7. Flags & Status
  { key: "PFApplicable", label: "PF Applicable", example: "YES", width: 18, category: "Flags", hint: "YES/NO" },
  { key: "ESIApplicable", label: "ESI Applicable", example: "YES", width: 18, category: "Flags", hint: "YES/NO" },

  // 8. Exit / Left Details
  { key: "LastWorking", label: "Last Working Day", type: "date", example: "2026-10-15", width: 18, category: "Exit Details", hint: "YYYY-MM-DD" },
  { key: "ESIReason", label: "ESI Left Reason", example: "Resigned", width: 20, category: "Exit Details", hint: "Reason of Left" },
  { key: "ESILast", label: "ESI Last Date", type: "date", example: "2026-10-15", width: 18, category: "Exit Details", hint: "YYYY-MM-DD" },
  { key: "PFReason", label: "PF Left Reason", example: "Resigned", width: 20, category: "Exit Details", hint: "Reason of Left" },
  { key: "PFLast", label: "PF Last Date", type: "date", example: "2026-10-15", width: 18, category: "Exit Details", hint: "YYYY-MM-DD" },
];

// Helper to generate formatted column header with placeholder brackets (identical to Employee Master style)
export const getFormattedColumnHeader = (field) => {
  if (!field) return "";
  const key = typeof field === "string" ? field : field.key;
  const label = typeof field === "string" ? field : field.label || field.key;
  
  // Clean base label by removing existing trailing brackets/parentheses
  let base = label.replace(/\s*\([^)]*\)$/, "").replace(/\s*\[[^\]]*\]$/, "").trim();

  // Custom hints in () based on field key describing exact expected values to enter
  if (key === "EmployeeCode" || key === "CardNo") return `${base} (Unique Code / Text)`;
  if (key === "EmployeeName" || key === "Name") return `${base} (Full Name / Text)`;
  if (key === "FatherName") return `${base} (Father Name / Text)`;
  if (key === "DepartmentName" || key === "Department") return `${base} (Department / Text)`;
  if (key === "DesignationName" || key === "Designation") return `${base} (Designation / Text)`;
  if (key === "BasicSalary") return `${base} (Amount / Must be > 0)`;
  if (key === "MonthDays") return `${base} (Number: 28-31)`;
  if (key === "WorkingDays") return `${base} (Days: 0-31)`;
  if (key === "PaidDay" || key === "PaidDays") return `${base} (Days: 0-31)`;
  if (key === "WorkingHours") return `${base} (Hours: 0-300)`;
  if (key === "WorkingMinutes") return `${base} (Minutes: 0-59)`;
  if (key === "OvertimeHours") return `${base} (Hours: 0-200)`;
  if (key === "OverTime") return `${base} (Amount / Number)`;
  if (key === "LeaveWages") return `${base} (Amount / Number)`;
  if (key === "FestivalHoliday") return `${base} (Days: 0-31)`;
  if (key === "WeeklyHoliday") return `${base} (Days: 0-31)`;
  if (key === "CL") return `${base} (CL / Days)`;
  if (key === "ML") return `${base} (ML / Days)`;
  if (key === "Arrear") return `${base} (Amount / Number)`;
  if (key === "AdvanceBonus") return `${base} (Amount / Number)`;
  if (key === "ExtraPay") return `${base} (Amount / Number)`;
  if (key === "ExtraPay1") return `${base} (Amount / Number)`;
  if (key === "Bonus") return `${base} (Amount / Number)`;
  if (key === "Gratuity") return `${base} (Amount / Number)`;
  if (key === "HRA") return `${base} (Amount / Number)`;
  if (key === "Conveyance") return `${base} (Amount / Number)`;
  if (key === "CCA") return `${base} (Amount / Number)`;
  if (key === "Medical") return `${base} (Amount / Number)`;
  if (key === "Retention") return `${base} (Amount / Number)`;
  if (key === "TotalAllowances") return `${base} (Amount / Calculated)`;
  if (key === "Wages") return `${base} (Amount / Calculated)`;
  if (key === "GrossAmount") return `${base} (Total Earnings / Amount)`;
  if (key === "PF") return `${base} (12% / Max 1800)`;
  if (key === "EmplrPF") return `${base} (Amount / 3.67% or 12%)`;
  if (key === "Pension") return `${base} (EPS 8.33% / Max 1250)`;
  if (key === "ArrearPF") return `${base} (Amount / Number)`;
  if (key === "ArrERPF") return `${base} (Amount / Number)`;
  if (key === "ArrPens") return `${base} (Amount / Number)`;
  if (key === "AdminCharges") return `${base} (Amount / Number)`;
  if (key === "AC21") return `${base} (EDLI 0.5% / Amount)`;
  if (key === "AC22") return `${base} (EDLI Admin / Amount)`;
  if (key === "ESI") return `${base} (0.75% / Amount)`;
  if (key === "EmplrESI") return `${base} (3.25% / Amount)`;
  if (key === "TDS") return `${base} (Tax / Amount)`;
  if (key === "Advance") return `${base} (Deduction / Amount)`;
  if (key === "Recovery") return `${base} (Deduction / Amount)`;
  if (key === "PFRefund") return `${base} (Amount / Number)`;
  if (key === "TotalDeductions") return `${base} (Other Deductions / Amount)`;
  if (key === "TotalDeduction") return `${base} (Total Deductions / Amount)`;
  if (key === "NetAmount") return `${base} (Net Payable / Amount)`;
  if (key === "PFApplicable" || key === "ESIApplicable") return `${base} (YES / NO)`;
  if (key === "LastWorking" || key === "ESILast" || key === "PFLast") return `${base} (Date: YYYY-MM-DD)`;
  if (key === "ESIReason" || key === "PFReason") return `${base} (Exit Reason / Text)`;

  if (typeof field === "object" && field.hint) {
    return `${base} (${field.hint})`;
  }
  if (typeof field === "object" && field.type === "number") {
    return `${base} (Amount / Number)`;
  }
  return `${base} (Text)`;
};

// ─────────────────────────────────────────────────────────────
// Dynamic Payroll Fields Builder matching Payroll Form & Payroll Parameters
// ─────────────────────────────────────────────────────────────
export const getDynamicPayrollFields = (payrollParams, allowanceMasterList = [], deductionMasterList = []) => {
  // Base fields that always exist in payroll form
  const fields = [
    { key: "EmployeeCode", label: "Employee Code", required: true, example: "0001", width: 18, category: "Identification", hint: "Unique Code / Text" },
    { key: "EmployeeName", label: "Employee Name", required: false, example: "Rahul Sharma", width: 24, category: "Identification", hint: "Full Name / Text" },
    { key: "FatherName", label: "Father Name", required: false, example: "Ramesh Sharma", width: 20, category: "Identification", hint: "Father Name / Text" },
    { key: "DepartmentName", label: "Department", required: false, example: "Production", width: 18, category: "Identification", hint: "Department / Text" },
    { key: "DesignationName", label: "Designation", required: false, example: "Supervisor", width: 18, category: "Identification", hint: "Designation / Text" },
    { key: "BasicSalary", label: "Basic Salary", required: true, type: "number", example: "15000", width: 16, category: "Salary", hint: "Amount / Must be > 0" },
    { key: "MonthDays", label: "Month Days", type: "number", example: "30", width: 14, category: "Attendance", hint: "Number: 28-31" },
    { key: "WorkingDays", label: "Working Days", type: "number", example: "26", width: 14, category: "Attendance", hint: "Days: 0-31" },
    { key: "PaidDay", label: "Paid Days", type: "number", example: "26", width: 14, category: "Attendance", hint: "Days: 0-31" },
  ];

  // Hourly payment conditional
  if (payrollParams?.IsHourlyPayment) {
    fields.push(
      { key: "WorkingHours", label: "Working Hours", type: "number", example: "208", width: 14, category: "Attendance", hint: "Hours: 0-300" },
      { key: "WorkingMinutes", label: "Working Mins", type: "number", example: "0", width: 14, category: "Attendance", hint: "Minutes: 0-59" }
    );
  }

  // Attendance leaves & holidays
  fields.push(
    { key: "LeaveWages", label: "Leave Wages", type: "number", example: "0", width: 14, category: "Salary", hint: "Amount / Number" },
    { key: "FestivalHoliday", label: "Festival Holiday", type: "number", example: "1", width: 16, category: "Attendance", hint: "Days: 0-31" },
    { key: "WeeklyHoliday", label: "Weekly Off", type: "number", example: "4", width: 14, category: "Attendance", hint: "Days: 0-31" },
    { key: "CL", label: "Casual Leave (CL)", type: "number", example: "0", width: 16, category: "Attendance", hint: "CL / Days" },
    { key: "ML", label: "Medical Leave (ML)", type: "number", example: "0", width: 16, category: "Attendance", hint: "ML / Days" }
  );

  // Overtime conditional
  if (payrollParams?.IsOvertime) {
    fields.push(
      { key: "OvertimeHours", label: "OT Hours", type: "number", example: "10", width: 14, category: "Attendance", hint: "Hours: 0-200" },
      { key: "OverTime", label: "OT Amount", type: "number", example: "1200", width: 14, category: "Salary", hint: "Amount / Number" }
    );
  }

  // Arrear conditional
  if (payrollParams?.IsArrearApplicable) {
    fields.push({ key: "Arrear", label: "Arrear", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number" });
  }

  // Advance Bonus conditional
  if (payrollParams?.IsAdvanceBonus) {
    fields.push({ key: "AdvanceBonus", label: "Advance Bonus", type: "number", example: "0", width: 16, category: "Earnings", hint: "Amount / Number" });
  }

  // Extra Pay 1 & 2 conditional
  if (payrollParams?.IsExtraPay) {
    const extraLabel = (payrollParams?.ExtraPayCaption || "").trim() || "Extra Pay 1";
    fields.push({ key: "ExtraPay", label: extraLabel, type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number" });
  }
  if (payrollParams?.IsExtraPay1) {
    const extra1Label = (payrollParams?.ExtraPay1Caption || "").trim() || "Extra Pay 2";
    fields.push({ key: "ExtraPay1", label: extra1Label, type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number" });
  }

  // Bonus & Gratuity conditional
  if (payrollParams?.IsBonusParameter) {
    fields.push({ key: "Bonus", label: "Bonus", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number" });
  }
  if (payrollParams?.IsGratuity) {
    fields.push({ key: "Gratuity", label: "Gratuity", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number" });
  }

  // Dynamic Firm Allowances (from AllowanceMaster)
  if (Array.isArray(allowanceMasterList) && allowanceMasterList.length > 0) {
    allowanceMasterList.forEach((a) => {
      const aName = a.Name || a.AllowanceName || `Allowance_${a.Id}`;
      fields.push({
        key: aName,
        label: aName,
        type: "number",
        example: "0",
        width: 16,
        category: "Earnings",
        hint: "Amount / Number",
        isAllowance: true,
        masterId: a.Id,
      });
    });
  } else if (!payrollParams) {
    fields.push(
      { key: "HRA", label: "HRA", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number", isAllowance: true },
      { key: "Conveyance", label: "Conveyance", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number", isAllowance: true },
      { key: "CCA", label: "CCA", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number", isAllowance: true },
      { key: "Medical", label: "Medical", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number", isAllowance: true },
      { key: "Retention", label: "Retention", type: "number", example: "0", width: 14, category: "Earnings", hint: "Amount / Number", isAllowance: true }
    );
  }

  // Earnings Totals
  fields.push(
    { key: "TotalAllowances", label: "Total Allowances", type: "number", example: "0", width: 16, category: "Earnings", hint: "Amount / Calculated" },
    { key: "Wages", label: "Earned Basic / Wages", type: "number", example: "15000", width: 22, category: "Earnings", hint: "Amount / Calculated" },
    { key: "GrossAmount", label: "Gross Amount", type: "number", example: "16200", width: 16, category: "Earnings", hint: "Total Earnings / Amount" }
  );

  // PF & Pension conditional
  if (payrollParams?.IsPF || !payrollParams) {
    fields.push(
      { key: "PF", label: "Employee PF", type: "number", example: "1800", width: 18, category: "Statutory", hint: "12% / Max 1800" },
      { key: "EmplrPF", label: "Employer PF", type: "number", example: "1800", width: 18, category: "Statutory", hint: "Amount / 3.67% or 12%" },
      { key: "Pension", label: "Pension (EPS)", type: "number", example: "1250", width: 16, category: "Statutory", hint: "EPS 8.33% / Max 1250" },
      { key: "ArrearPF", label: "Arrear PF", type: "number", example: "0", width: 14, category: "Statutory", hint: "Amount / Number" },
      { key: "ArrERPF", label: "Arr ER PF", type: "number", example: "0", width: 14, category: "Statutory", hint: "Amount / Number" },
      { key: "ArrPens", label: "Arr Pension", type: "number", example: "0", width: 14, category: "Statutory", hint: "Amount / Number" },
      { key: "AdminCharges", label: "Admin Charges", type: "number", example: "0", width: 16, category: "Statutory", hint: "Amount / Number" },
      { key: "AC21", label: "AC 21 (EDLI)", type: "number", example: "0", width: 16, category: "Statutory", hint: "EDLI 0.5% / Amount" },
      { key: "AC22", label: "AC 22 (EDLI Admin)", type: "number", example: "0", width: 18, category: "Statutory", hint: "EDLI Admin / Amount" }
    );
  }

  // ESI conditional
  if (payrollParams?.IsESI || !payrollParams) {
    fields.push(
      { key: "ESI", label: "Employee ESI", type: "number", example: "121.50", width: 18, category: "Statutory", hint: "0.75% / Amount" },
      { key: "EmplrESI", label: "Employer ESI", type: "number", example: "526.50", width: 18, category: "Statutory", hint: "3.25% / Amount" }
    );
  }

  // Deductions conditional
  if (payrollParams?.IsTDS) {
    fields.push({ key: "TDS", label: "TDS", type: "number", example: "0", width: 12, category: "Deductions", hint: "Tax / Amount" });
  }
  if (payrollParams?.IsAdvance) {
    fields.push({ key: "Advance", label: "Advance", type: "number", example: "0", width: 14, category: "Deductions", hint: "Deduction / Amount" });
  }
  if (payrollParams?.IsRecovery) {
    fields.push({ key: "Recovery", label: "Recovery", type: "number", example: "0", width: 14, category: "Deductions", hint: "Deduction / Amount" });
  }
  if (payrollParams?.IsPFAdvRefund) {
    fields.push({ key: "PFRefund", label: "PF Refund", type: "number", example: "0", width: 14, category: "Deductions", hint: "Amount / Number" });
  }

  // Dynamic Firm Deductions (from DeductionMaster)
  if (Array.isArray(deductionMasterList) && deductionMasterList.length > 0) {
    deductionMasterList.forEach((d) => {
      const dName = d.Name || d.DeductionName || `Deduction_${d.Id}`;
      fields.push({
        key: dName,
        label: dName,
        type: "number",
        example: "0",
        width: 16,
        category: "Deductions",
        hint: "Deduction / Amount",
        isDeduction: true,
        masterId: d.Id,
      });
    });
  }

  // Deductions Totals & Net Pay (always visible)
  fields.push(
    { key: "TotalDeductions", label: "Total Other Deductions", type: "number", example: "0", width: 20, category: "Deductions", hint: "Other Deductions / Amount" },
    { key: "TotalDeduction", label: "Total Deductions", type: "number", example: "1921.50", width: 18, category: "Deductions", hint: "Total Deductions / Amount" },
    { key: "NetAmount", label: "Net Payable Amount", type: "number", example: "14278.50", width: 20, category: "Summary", hint: "Net Payable / Amount" }
  );

  // Flags & Status if PF / ESI enabled
  if (payrollParams?.IsPF || !payrollParams) {
    fields.push({ key: "PFApplicable", label: "PF Applicable", example: "YES", width: 18, category: "Flags", hint: "YES / NO" });
  }
  if (payrollParams?.IsESI || !payrollParams) {
    fields.push({ key: "ESIApplicable", label: "ESI Applicable", example: "YES", width: 18, category: "Flags", hint: "YES / NO" });
  }

  // Exit / Left Details (for settling employees leaving this month)
  fields.push(
    { key: "LastWorking", label: "Last Working Day", type: "date", example: "2026-10-15", width: 18, category: "Exit Details", hint: "YYYY-MM-DD" },
    { key: "ESIReason", label: "ESI Left Reason", example: "Resigned", width: 20, category: "Exit Details", hint: "Reason of Left" },
    { key: "ESILast", label: "ESI Last Date", type: "date", example: "2026-10-15", width: 18, category: "Exit Details", hint: "YYYY-MM-DD" },
    { key: "PFReason", label: "PF Left Reason", example: "Resigned", width: 20, category: "Exit Details", hint: "Reason of Left" },
    { key: "PFLast", label: "PF Last Date", type: "date", example: "2026-10-15", width: 18, category: "Exit Details", hint: "YYYY-MM-DD" }
  );

  return fields;
};

export const parseNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = parseFloat(String(v).replace(/,/g, "").trim());
  return isNaN(n) ? 0 : n;
};

export const parseBool = (v) => {
  if (v === true || v === 1 || v === "1") return true;
  const s = String(v).trim().toUpperCase();
  if (s === "TRUE" || s === "YES" || s === "Y") return true;
  return false;
};

// Safe field value extractor from row (handles dynamic allowances/deductions and aliases)
export const getFieldValueFromRow = (row, field) => {
  if (!row || !field) return "";
  const key = typeof field === "string" ? field : field.key;
  const label = typeof field === "object" ? field.label : "";
  const isNumber = typeof field === "object" ? field.type === "number" : false;

  if (key === "EmployeeCode") {
    return row.EmployeeCode || row.CardNo || row.SerialNo || "";
  }
  if (key === "CardNo") {
    return row.CardNo || row.EmployeeCode || "";
  }
  if (key === "EmployeeName") {
    return row.EmployeeName || row.Name || "";
  }
  if (key === "FatherName") {
    return row.FatherName || row.Father || "";
  }
  if (key === "DepartmentName") {
    return row.DepartmentName || row.Department || "";
  }
  if (key === "DesignationName") {
    return row.DesignationName || row.Designation || "";
  }
  if (key === "PFApplicable" || key === "ESIApplicable") {
    const val = row[key];
    return parseBool(val) ? "YES" : "NO";
  }

  // 1. Direct match on key or label
  let val = row[key];
  if ((val === undefined || val === null || val === "") && label) {
    val = row[label];
  }

  // 2. Allowance1..10 and Deduction1..10 matching
  if (val === undefined || val === null || val === "" || val === 0) {
    const lk = key.toLowerCase();
    for (let i = 1; i <= 10; i++) {
      const aName = row[`Allowance${i}Name`];
      if (aName && (aName.trim().toLowerCase() === lk || (label && aName.trim().toLowerCase() === label.toLowerCase()))) {
        val = row[`CalculatedAllowance${i}`] !== undefined && row[`CalculatedAllowance${i}`] !== null ? row[`CalculatedAllowance${i}`] : row[`OriginalAllowance${i}`];
        break;
      }
    }
  }
  if (val === undefined || val === null || val === "" || val === 0) {
    const lk = key.toLowerCase();
    for (let i = 1; i <= 10; i++) {
      const dName = row[`Deduction${i}Name`];
      if (dName && (dName.trim().toLowerCase() === lk || (label && dName.trim().toLowerCase() === label.toLowerCase()))) {
        val = row[`CalculatedDeduction${i}`] !== undefined && row[`CalculatedDeduction${i}`] !== null ? row[`CalculatedDeduction${i}`] : row[`OriginalDeduction${i}`];
        break;
      }
    }
  }

  // 3. Case-insensitive key scan
  if (val === undefined || val === null || val === "") {
    const lowerKey = key.toLowerCase();
    for (const k of Object.keys(row)) {
      if (k.toLowerCase() === lowerKey && row[k] !== undefined && row[k] !== null && row[k] !== "") {
        val = row[k];
        break;
      }
    }
  }

  // 4. Common specific aliases
  if (val === undefined || val === null || val === "") {
    const lowerKey = key.toLowerCase();
    if (lowerKey === "conv" && row.Conveyance !== undefined) val = row.Conveyance;
    else if (lowerKey === "conveyance" && row.CONV !== undefined) val = row.CONV;
    else if (lowerKey === "otamount" && row.OverTime !== undefined) val = row.OverTime;
    else if (lowerKey === "overtime" && row.OverTime !== undefined) val = row.OverTime;
  }

  if (isNumber) {
    return parseNum(val);
  }
  return val !== undefined && val !== null ? String(val) : "";
};

// ─────────────────────────────────────────────────────────────
// Native Excel Formula Engine (Microprogramming) for Dynamic Payroll
// ─────────────────────────────────────────────────────────────
export const colIndexToLetter = (colIndex) => {
  let temp = colIndex;
  let letter = "";
  while (temp > 0) {
    const rem = (temp - 1) % 26;
    letter = String.fromCharCode(65 + rem) + letter;
    temp = Math.floor((temp - 1) / 26);
  }
  return letter;
};

export const generateRowValuesWithFormulas = (
  row,
  exportFields,
  rowNum,
  allowanceMasterList = [],
  deductionMasterList = [],
  mode = "saved", // 'saved' | 'active' | 'blank'
  payrollParams = null
) => {
  const colLetterMap = {};
  exportFields.forEach((f, idx) => {
    colLetterMap[f.key] = colIndexToLetter(idx + 1);
  });

  const getCol = (key) => colLetterMap[key];

  const getRaw = (key) => {
    const fDef = exportFields.find((f) => f.key === key) || { key };
    return getFieldValueFromRow(row, fDef);
  };

  const isBlank = mode === "blank";
  const isActive = mode === "active";

  // For unentered active staff export, working days is 0 (ready for attendance entry)
  let workingDaysVal = parseNum(getRaw("WorkingDays"));
  if (isActive && (!workingDaysVal || workingDaysVal === parseNum(getRaw("MonthDays")))) {
    workingDaysVal = 0;
  }

  return exportFields.map((f) => {
    const key = f.key;

    // Non-numeric columns
    if (f.type !== "number") {
      let val = getRaw(key);
      if (isBlank && f.example) val = f.example;
      return val !== undefined && val !== null ? String(val) : "";
    }

    // Attendance inputs
    if (key === "MonthDays") {
      return parseNum(getRaw("MonthDays")) || 31;
    }
    if (key === "WorkingDays") {
      if (isBlank) return 0;
      return workingDaysVal;
    }
    if (key === "LeaveWages" || key === "FestivalHoliday" || key === "CL" || key === "ML") {
      return parseNum(getRaw(key));
    }
    if (key === "WeeklyHoliday") {
      return parseNum(getRaw(key));
    }

    // ── FORMULA: PaidDay ───────────────────────────────────────────
    if (key === "PaidDay") {
      const parts = [
        getCol("WorkingDays"),
        getCol("LeaveWages"),
        getCol("FestivalHoliday"),
        getCol("WeeklyHoliday"),
        getCol("CL"),
        getCol("ML"),
      ].filter(Boolean).map((c) => `${c}${rowNum}`);

      const mCol = getCol("MonthDays");
      const monthRef = mCol ? `${mCol}${rowNum}` : "31";

      const currentPaid = Math.min(
        parseNum(getRaw("MonthDays")) || 31,
        workingDaysVal +
          parseNum(getRaw("LeaveWages")) +
          parseNum(getRaw("FestivalHoliday")) +
          parseNum(getRaw("WeeklyHoliday")) +
          parseNum(getRaw("CL")) +
          parseNum(getRaw("ML"))
      );

      if (parts.length > 0) {
        return {
          formula: `MIN(${monthRef}, ${parts.join(" + ")})`,
          result: currentPaid,
        };
      }
      return currentPaid;
    }

    // Basic Salary & Hours
    if (key === "BasicSalary") return parseNum(getRaw("BasicSalary"));
    if (key === "WorkingHours") return parseNum(getRaw("WorkingHours"));
    if (key === "WorkingMinutes") return parseNum(getRaw("WorkingMinutes"));
    if (key === "OvertimeHours") return parseNum(getRaw("OvertimeHours"));

    // ── FORMULA: OverTime Amount ──────────────────────────────────
    if (key === "OverTime") {
      const basicCol = getCol("BasicSalary");
      const monthCol = getCol("MonthDays");
      const otHoursCol = getCol("OvertimeHours");
      const currentOT = parseNum(getRaw("OverTime"));

      if (basicCol && monthCol && otHoursCol) {
        const isOTOn26 = !!(
          payrollParams?.IsOTOn26Days ||
          payrollParams?.OvertimeOn26Days ||
          payrollParams?.IsOvertimeOn26Days ||
          String(payrollParams?.IsOTOn26Days).toLowerCase() === "true" ||
          String(payrollParams?.OvertimeOn26Days).toLowerCase() === "true"
        );
        const isDoubleOT = !!(
          payrollParams?.IsDoubleOT ||
          payrollParams?.DoubleOvertimeAmount ||
          payrollParams?.IsDoubleOvertimeAmount ||
          String(payrollParams?.IsDoubleOT).toLowerCase() === "true"
        );
        const stdHours = parseFloat(payrollParams?.WorkingHours) || 8;
        const mult = isDoubleOT ? 2 : 1;
        const divisorRef = isOTOn26 ? "26" : `${monthCol}${rowNum}`;

        const formula = mult > 1
          ? `ROUND(((${basicCol}${rowNum} / ${divisorRef} / ${stdHours}) * ${mult}) * ${otHoursCol}${rowNum}, 2)`
          : `ROUND((${basicCol}${rowNum} / ${divisorRef} / ${stdHours}) * ${otHoursCol}${rowNum}, 2)`;

        return {
          formula,
          result: currentOT,
        };
      }
      return currentOT;
    }

    // ── Dynamic Allowances ─────────────────────────────────────────
    const isAllowanceField = f.isAllowance || (allowanceMasterList || []).some((a) => (a.Name || a.AllowanceName) === key);
    if (isAllowanceField) {
      return parseNum(getRaw(key));
    }

    // ── FORMULA: TotalAllowances ──────────────────────────────────
    if (key === "TotalAllowances") {
      const allowCols = exportFields
        .filter((cf) => cf.isAllowance || (allowanceMasterList || []).some((a) => (a.Name || a.AllowanceName) === cf.key))
        .map((cf) => `${colLetterMap[cf.key]}${rowNum}`);

      const currentTotalAl = parseNum(getRaw("TotalAllowances"));
      if (allowCols.length > 0) {
        return {
          formula: `ROUND(SUM(${allowCols.join(",")}), 2)`,
          result: currentTotalAl,
        };
      }
      return 0;
    }

    // ── FORMULA: Wages (Earned Basic) ──────────────────────────────
    if (key === "Wages") {
      const basicCol = getCol("BasicSalary");
      const paidCol = getCol("PaidDay");
      const monthCol = getCol("MonthDays");
      const mDays = parseNum(getRaw("MonthDays")) || 31;
      const bSal = parseNum(getRaw("BasicSalary"));
      const pDays = Math.min(
        mDays,
        workingDaysVal +
          parseNum(getRaw("LeaveWages")) +
          parseNum(getRaw("FestivalHoliday")) +
          parseNum(getRaw("WeeklyHoliday")) +
          parseNum(getRaw("CL")) +
          parseNum(getRaw("ML"))
      );
      const currentWages = mDays > 0 ? Math.round(((bSal * pDays) / mDays) * 100) / 100 : 0;

      if (basicCol && paidCol && monthCol) {
        return {
          formula: `ROUND((${basicCol}${rowNum} * ${paidCol}${rowNum}) / ${monthCol}${rowNum}, 2)`,
          result: currentWages,
        };
      }
      return currentWages;
    }

    // ── FORMULA: GrossAmount ───────────────────────────────────────
    if (key === "GrossAmount") {
      const grossParts = [
        getCol("Wages"),
        getCol("TotalAllowances"),
        getCol("OverTime"),
        getCol("Arrear"),
        getCol("ExtraPay"),
        getCol("ExtraPay1"),
        getCol("AdvanceBonus"),
        getCol("Bonus"),
      ].filter(Boolean).map((c) => `${c}${rowNum}`);

      const currentGross = parseNum(getRaw("GrossAmount"));
      if (grossParts.length > 0) {
        return {
          formula: `ROUND(${grossParts.join(" + ")}, 2)`,
          result: currentGross,
        };
      }
      return currentGross;
    }

    // ── Dynamic Deductions ─────────────────────────────────────────
    const isDeductionField = f.isDeduction || (deductionMasterList || []).some((d) => (d.Name || d.DeductionName) === key);
    if (isDeductionField) {
      return parseNum(getRaw(key));
    }

    // ── FORMULA: TotalOtherDeductions (TotalDeductions) ────────────
    if (key === "TotalDeductions") {
      const dedCols = exportFields
        .filter((cf) => cf.isDeduction || (deductionMasterList || []).some((d) => (d.Name || d.DeductionName) === cf.key))
        .map((cf) => `${colLetterMap[cf.key]}${rowNum}`);

      const currentTotalOtherDed = parseNum(getRaw("TotalDeductions"));
      if (dedCols.length > 0) {
        return {
          formula: `ROUND(SUM(${dedCols.join(",")}), 2)`,
          result: currentTotalOtherDed,
        };
      }
      return 0;
    }

    // ── FORMULA: PF (if present) ──────────────────────────────────
    if (key === "PF") {
      const wagesCol = getCol("Wages");
      const currentPF = parseNum(getRaw("PF"));
      if (wagesCol && parseBool(row.PFApplicable ?? true)) {
        return {
          formula: `ROUND(MIN(${wagesCol}${rowNum}, 15000) * 0.12, 0)`,
          result: currentPF,
        };
      }
      return currentPF;
    }

    // ── FORMULA: EmplrPF (if present) ─────────────────────────────
    if (key === "EmplrPF") {
      const wagesCol = getCol("Wages");
      const currentEmplrPF = parseNum(getRaw("EmplrPF"));
      if (wagesCol && parseBool(row.PFApplicable ?? true)) {
        return {
          formula: `ROUND(MIN(${wagesCol}${rowNum}, 15000) * 0.0367, 0)`,
          result: currentEmplrPF,
        };
      }
      return currentEmplrPF;
    }

    // ── FORMULA: Pension (if present) ─────────────────────────────
    if (key === "Pension") {
      const wagesCol = getCol("Wages");
      const currentPension = parseNum(getRaw("Pension"));
      if (wagesCol && parseBool(row.PFApplicable ?? true) && parseBool(row.PensionApplicable ?? true)) {
        return {
          formula: `MIN(ROUND(MIN(${wagesCol}${rowNum}, 15000) * 0.0833, 0), 1250)`,
          result: currentPension,
        };
      }
      return currentPension;
    }

    // ── FORMULA: ESI (if present) ─────────────────────────────────
    if (key === "ESI") {
      const grossCol = getCol("GrossAmount");
      const paidCol = getCol("PaidDay");
      const otCol = getCol("OverTime");
      const currentESI = parseNum(getRaw("ESI"));
      const isPwd = parseBool(row.IsHandicap);
      const ceiling = isPwd ? 25000 : 21000;
      const isFullESI = !!(
        payrollParams?.IsFullESIEmployer ||
        payrollParams?.FullESIByEmployer ||
        String(payrollParams?.IsFullESIEmployer).toLowerCase() === "true" ||
        payrollParams?.IsFullESIEmployer === 1
      );

      if (isFullESI) {
        return {
          formula: "0",
          result: 0,
        };
      }

      if (grossCol && parseBool(row.ESIApplicable ?? true)) {
        const eligExpr = otCol ? `(${grossCol}${rowNum} - ${otCol}${rowNum}) <= ${ceiling}` : `${grossCol}${rowNum} <= ${ceiling}`;
        const formulaStr = paidCol
          ? `IF(AND(${paidCol}${rowNum} > 0, (${grossCol}${rowNum} / ${paidCol}${rowNum}) <= 176), 0, IF(${eligExpr}, ROUNDUP(${grossCol}${rowNum} * 0.0075, 0), 0))`
          : `IF(${eligExpr}, ROUNDUP(${grossCol}${rowNum} * 0.0075, 0), 0)`;
        return {
          formula: formulaStr,
          result: currentESI,
        };
      }
      return currentESI;
    }

    // ── FORMULA: EmplrESI (if present) ────────────────────────────
    if (key === "EmplrESI") {
      const grossCol = getCol("GrossAmount");
      const otCol = getCol("OverTime");
      const currentEmplrESI = parseNum(getRaw("EmplrESI"));
      const isPwd = parseBool(row.IsHandicap);
      const ceiling = isPwd ? 25000 : 21000;
      const isFullESI = !!(
        payrollParams?.IsFullESIEmployer ||
        payrollParams?.FullESIByEmployer ||
        String(payrollParams?.IsFullESIEmployer).toLowerCase() === "true" ||
        payrollParams?.IsFullESIEmployer === 1
      );
      const erRate = isFullESI ? 0.04 : 0.0325;

      if (grossCol && parseBool(row.ESIApplicable ?? true)) {
        const eligExpr = otCol ? `(${grossCol}${rowNum} - ${otCol}${rowNum}) <= ${ceiling}` : `${grossCol}${rowNum} <= ${ceiling}`;
        return {
          formula: `IF(${eligExpr}, ROUNDUP(${grossCol}${rowNum} * ${erRate}, 0), 0)`,
          result: currentEmplrESI,
        };
      }
      return currentEmplrESI;
    }

    // Other numeric inputs
    if (["ArrearPF", "ArrERPF", "ArrPens", "AdminCharges", "AC21", "AC22", "TDS", "Advance", "Recovery", "PFRefund", "Arrear", "AdvanceBonus", "ExtraPay", "ExtraPay1", "Bonus", "Gratuity"].includes(key)) {
      return parseNum(getRaw(key));
    }

    // ── FORMULA: TotalDeduction ────────────────────────────────────
    if (key === "TotalDeduction") {
      const dedParts = [
        getCol("PF"),
        getCol("ESI"),
        getCol("TDS"),
        getCol("Advance"),
        getCol("Recovery"),
        getCol("TotalDeductions"),
      ].filter(Boolean).map((c) => `${c}${rowNum}`);

      const currentTotalDed = parseNum(getRaw("TotalDeduction"));
      if (dedParts.length > 0) {
        return {
          formula: `ROUND(${dedParts.join(" + ")}, 2)`,
          result: currentTotalDed,
        };
      }
      return currentTotalDed;
    }

    // ── FORMULA: NetAmount ─────────────────────────────────────────
    if (key === "NetAmount") {
      const grossCol = getCol("GrossAmount");
      const dedCol = getCol("TotalDeduction");
      const pfRefCol = getCol("PFRefund");
      const currentNet = parseNum(getRaw("NetAmount"));

      if (grossCol && dedCol) {
        if (pfRefCol) {
          return {
            formula: `ROUND(${grossCol}${rowNum} - ${dedCol}${rowNum} + ${pfRefCol}${rowNum}, 2)`,
            result: currentNet,
          };
        }
        return {
          formula: `ROUND(${grossCol}${rowNum} - ${dedCol}${rowNum}, 2)`,
          result: currentNet,
        };
      }
      return currentNet;
    }

    return parseNum(getRaw(key));
  });
};

// ─────────────────────────────────────────────────────────────
// Explicit Alias Dictionary for Unambiguous Header Matching
// ─────────────────────────────────────────────────────────────
const PAYROLL_HEADER_ALIASES = {
  // Employee matching
  "employeecode": "EmployeeCode",
  "employee code": "EmployeeCode",
  "emp code": "EmployeeCode",
  "empcode": "EmployeeCode",
  "emp no": "EmployeeCode",
  "empno": "EmployeeCode",
  "cardno": "EmployeeCode",
  "card no": "EmployeeCode",
  "card number": "EmployeeCode",
  "serialno": "EmployeeCode",
  "serial no": "EmployeeCode",
  "code": "EmployeeCode",

  "employeename": "EmployeeName",
  "employee name": "EmployeeName",
  "emp name": "EmployeeName",
  "name": "EmployeeName",
  "employee": "EmployeeName",

  "father name": "FatherName",
  "father's name": "FatherName",
  "father’s name": "FatherName",
  "fathers name": "FatherName",
  "fathername": "FatherName",
  "father": "FatherName",
  "f/name": "FatherName",
  "fname": "FatherName",

  "department": "DepartmentName",
  "departmentname": "DepartmentName",
  "dept": "DepartmentName",
  "dept name": "DepartmentName",

  "designation": "DesignationName",
  "designationname": "DesignationName",
  "desig": "DesignationName",
  "desig name": "DesignationName",

  // Basic & Days
  "basicsalary": "BasicSalary",
  "basic salary": "BasicSalary",
  "basic pay": "BasicSalary",
  "basic wage": "BasicSalary",
  "basic": "BasicSalary",

  "monthdays": "MonthDays",
  "month days": "MonthDays",
  "days in month": "MonthDays",
  "total days": "MonthDays",

  "workingdays": "WorkingDays",
  "working days": "WorkingDays",
  "duty days": "WorkingDays",

  "paidday": "PaidDay",
  "paid days": "PaidDay",
  "paid day": "PaidDay",
  "paiddays": "PaidDay",
  "payable days": "PaidDay",
  "present days": "PaidDay",

  "workinghours": "WorkingHours",
  "working hours": "WorkingHours",
  "duty hours": "WorkingHours",

  "workingminutes": "WorkingMinutes",
  "working mins": "WorkingMinutes",
  "working minutes": "WorkingMinutes",

  "overtimehours": "OvertimeHours",
  "ot hours": "OvertimeHours",
  "overtime hours": "OvertimeHours",
  "ot hrs": "OvertimeHours",

  "overtime": "OverTime",
  "ot amount": "OverTime",
  "ot amt": "OverTime",
  "over time": "OverTime",
  "overtime amount": "OverTime",

  "leavewages": "LeaveWages",
  "leave wages": "LeaveWages",
  "leave wage": "LeaveWages",

  "festivalholiday": "FestivalHoliday",
  "festival holiday": "FestivalHoliday",
  "festival off": "FestivalHoliday",
  "fh": "FestivalHoliday",

  "weeklyholiday": "WeeklyHoliday",
  "weekly off": "WeeklyHoliday",
  "weekly holiday": "WeeklyHoliday",
  "wo": "WeeklyHoliday",

  "cl": "CL",
  "casual leave": "CL",
  "casual leave cl": "CL",

  "ml": "ML",
  "medical leave": "ML",
  "medical leave ml": "ML",

  // Earnings & Allowances
  "arrear": "Arrear",
  "salary arrear": "Arrear",
  "arrears": "Arrear",

  "advancebonus": "AdvanceBonus",
  "adv bonus": "AdvanceBonus",
  "advance bonus": "AdvanceBonus",

  "extrapay": "ExtraPay",
  "extra pay": "ExtraPay",
  "extra pay 1": "ExtraPay",

  "extrapay1": "ExtraPay1",
  "extra pay 2": "ExtraPay1",

  "bonus": "Bonus",
  "statutory bonus": "Bonus",

  "gratuity": "Gratuity",

  "hra": "HRA",
  "house rent allowance": "HRA",

  "conveyance": "Conveyance",
  "conveyance allowance": "Conveyance",

  "cca": "CCA",
  "city compensatory allowance": "CCA",

  "medical": "Medical",
  "medical allowance": "Medical",

  "retention": "Retention",
  "retention allowance": "Retention",

  "totalallowances": "TotalAllowances",
  "total allowances": "TotalAllowances",
  "allowances total": "TotalAllowances",

  "wages": "Wages",
  "earned basic": "Wages",
  "earned basic wages": "Wages",
  "earned wage": "Wages",
  "earned basic / wages": "Wages",

  "grossamount": "GrossAmount",
  "gross amount": "GrossAmount",
  "gross": "GrossAmount",
  "gross salary": "GrossAmount",
  "gross wages": "GrossAmount",

  // PF & Statutory (Explicit disambiguation)
  "employee pf": "PF",
  "employeepf": "PF",
  "pf employee": "PF",
  "pf (employee)": "PF",
  "pf (emp)": "PF",
  "epf (employee)": "PF",
  "pf": "PF",
  "epf": "PF",

  "employer pf": "EmplrPF",
  "employerpf": "EmplrPF",
  "pf (employer)": "EmplrPF",
  "pf employer": "EmplrPF",
  "emplrpf": "EmplrPF",
  "emplr pf": "EmplrPF",
  "er pf": "EmplrPF",
  "erpf": "EmplrPF",
  "pf (er)": "EmplrPF",

  "pension": "Pension",
  "pension (eps)": "Pension",
  "eps": "Pension",
  "eps pension": "Pension",
  "pension fund": "Pension",

  "arrearpf": "ArrearPF",
  "arrear pf": "ArrearPF",
  "arr pf": "ArrearPF",

  "arrerpf": "ArrERPF",
  "arr er pf": "ArrERPF",
  "arrear employer pf": "ArrERPF",

  "arrpens": "ArrPens",
  "arr pens": "ArrPens",
  "arr pension": "ArrPens",
  "arrear pension": "ArrPens",

  "admincharges": "AdminCharges",
  "admin charges": "AdminCharges",
  "pf admin": "AdminCharges",
  "pf admin charges": "AdminCharges",

  "ac21": "AC21",
  "ac 21": "AC21",
  "edli": "AC21",
  "ac 21 (edli)": "AC21",

  "ac22": "AC22",
  "ac 22": "AC22",
  "edli admin": "AC22",
  "ac 22 (edli admin)": "AC22",

  // ESI Statutory (Explicit disambiguation)
  "employee esi": "ESI",
  "employeeesi": "ESI",
  "esi (employee)": "ESI",
  "esi (emp)": "ESI",
  "esi employee": "ESI",
  "esi": "ESI",
  "esic": "ESI",

  "employer esi": "EmplrESI",
  "employeresi": "EmplrESI",
  "esi (employer)": "EmplrESI",
  "esi employer": "EmplrESI",
  "emplresi": "EmplrESI",
  "emplr esi": "EmplrESI",
  "er esi": "EmplrESI",
  "eresi": "EmplrESI",
  "esi (er)": "EmplrESI",

  // Deductions
  "tds": "TDS",
  "tax": "TDS",
  "income tax": "TDS",

  "advance": "Advance",
  "salary advance": "Advance",
  "adv": "Advance",

  "recovery": "Recovery",
  "loan recovery": "Recovery",

  "pfrefund": "PFRefund",
  "pf refund": "PFRefund",

  "totaldeductions": "TotalDeductions",
  "total other deductions": "TotalDeductions",
  "other deductions": "TotalDeductions",

  "totaldeduction": "TotalDeduction",
  "total deductions": "TotalDeduction",
  "total ded": "TotalDeduction",

  "netamount": "NetAmount",
  "net amount": "NetAmount",
  "net salary": "NetAmount",
  "net pay": "NetAmount",
  "net payable": "NetAmount",
  "net payable amount": "NetAmount",

  // Flags
  "pfapplicable": "PFApplicable",
  "pf applicable": "PFApplicable",
  "pf apply": "PFApplicable",

  "esiapplicable": "ESIApplicable",
  "esi applicable": "ESIApplicable",
  "esi apply": "ESIApplicable",
};

// Guidelines for Field Guide Modal
const FORMAT_GUIDELINES = [
  {
    category: "🔑 Employee Identification & Matching",
    fields: [
      { field: "Employee Code [EmployeeCode]", format: "Text / Number", allowed: "Existing employee code (e.g. 0001, 102)", rule: "MANDATORY MATCHING KEY. Matches active employee in the firm by Employee Code. If employee not found, row is marked invalid.", example: "0001" },
      { field: "Employee Name [EmployeeName]", format: "Text (Optional)", allowed: "Employee Full Name", rule: "Used for display/cross-checking. If Employee Code is absent, system will attempt matching by Name.", example: "Rahul Sharma" },
      { field: "Department & Designation", format: "Master / Text", allowed: "Existing Department / Designation name", rule: "Informational master classifications from employee profile.", example: "Production / Operator" },
    ]
  },
  {
    category: "📅 Attendance & Days",
    fields: [
      { field: "Month Days [MonthDays]", format: "Number (28-31)", allowed: "28, 29, 30, 31", rule: "Total days in selected month.", example: "30" },
      { field: "Working Days [WorkingDays]", format: "Number (0-31)", allowed: "0 - 31", rule: "Standard expected working days for the employee.", example: "26" },
      { field: "Paid Days [PaidDay]", format: "Number (0-31)", allowed: "0 - 31", rule: "Actual attendance days to be paid.", example: "26" },
      { field: "Overtime Hours [OvertimeHours]", format: "Number (Hours)", allowed: "0 - 200", rule: "Total overtime hours worked.", example: "10" },
      { field: "Leave / Holidays", format: "Number (Days)", allowed: "0 - 31", rule: "FestivalHoliday, WeeklyHoliday, CL, ML days count.", example: "1" },
    ]
  },
  {
    category: "💰 Salary & Earnings",
    fields: [
      { field: "Basic Salary [BasicSalary]", format: "Currency / Number", allowed: "Positive numbers (> 0)", rule: "MANDATORY. Employee basic salary wage rate. Cannot be 0 or negative.", example: "15000" },
      { field: "Earned Wages [Wages]", format: "Currency / Number", allowed: "Positive numbers", rule: "Pro-rated basic salary earned for paid days.", example: "15000" },
      { field: "Overtime Amount [OverTime]", format: "Currency / Number", allowed: "Positive numbers", rule: "Overtime earnings.", example: "1200" },
      { field: "Gross Amount [GrossAmount]", format: "Currency / Number", allowed: "Positive numbers", rule: "Total gross earnings before deductions. If blank, auto-calculated as Wages + Allowances + OT + Arrear + ExtraPay + Bonus.", example: "16200" },
    ]
  },
  {
    category: "🏦 Statutory PF & Pension (EPFO)",
    fields: [
      { field: "Employee PF [PF]", format: "Currency / Number", allowed: "12% of PF wage (up to limit)", rule: "Employee share of Provident Fund contribution deducted from gross salary.", example: "1800" },
      { field: "Employer PF [EmplrPF]", format: "Currency / Number", allowed: "3.67% or 12% contribution", rule: "Employer statutory contribution towards EPF.", example: "1800" },
      { field: "Pension [Pension]", format: "Currency / Number", allowed: "8.33% up to ceiling 1250", rule: "Employer statutory contribution towards EPS Pension.", example: "1250" },
      { field: "Arrear PF / ArrPens", format: "Currency / Number", allowed: "Positive numbers", rule: "Arrear PF and Arrear Pension adjustments.", example: "0" },
      { field: "Admin Charges / AC21 / AC22", format: "Currency / Number", allowed: "EPFO admin and EDLI inspection fees", rule: "PF administration and insurance charges.", example: "75" },
    ]
  },
  {
    category: "🏥 Statutory ESI (ESIC)",
    fields: [
      { field: "Employee ESI [ESI]", format: "Currency / Number", allowed: "0.75% of Gross Wages", rule: "Employee statutory health insurance contribution deducted from salary.", example: "121.50" },
      { field: "Employer ESI [EmplrESI]", format: "Currency / Number", allowed: "3.25% of Gross Wages", rule: "Employer statutory health insurance contribution.", example: "526.50" },
    ]
  },
  {
    category: "✂️ Other Deductions & Net Pay",
    fields: [
      { field: "TDS / Advance / Recovery", format: "Currency / Number", allowed: "Positive numbers", rule: "Income tax TDS, Salary advances, Loan/Other recoveries.", example: "500" },
      { field: "Total Deductions [TotalDeduction]", format: "Currency / Number", allowed: "Sum of all deductions", rule: "PF + ESI + TDS + Advance + Recovery + Other Deductions.", example: "1921.50" },
      { field: "Net Amount [NetAmount]", format: "Currency / Number", allowed: "Gross minus Total Deductions", rule: "Net take-home payable amount disbursed to employee.", example: "14278.50" },
      { field: "PF / ESI Applicable", format: "Dropdown / Text (YES/NO)", allowed: "YES, NO", rule: "Flags indicating statutory coverage for the employee.", example: "YES" },
    ]
  }
];

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────
export const PayrollImportExport = () => {
  const navigate = useNavigate();

  const { firm: sessionFirm, firmId: sessionFirmId, month: sessionMonth, year: sessionYear } = useSession();
  const storedFirm = (() => {
    try {
      return JSON.parse(localStorage.getItem("selectedFirm") || "{}");
    } catch {
      return {};
    }
  })();
  const selectedFirm = sessionFirm?.Id ? sessionFirm : storedFirm;
  const firmId = selectedFirm?.Id ? String(selectedFirm.Id) : (sessionFirmId ? String(sessionFirmId) : "0");

  // Auth token
  const authUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("userDetails") || "{}");
    } catch {
      return {};
    }
  })();
  const authToken = authUser?.token || API_WEB_URLS.Token;
  const userId = authUser?.Id ? String(authUser.Id) : "1";

  // Month & Year state (synchronized with Top Header / useSession)
  const [selectedMonth, setSelectedMonth] = useState(() => {
    return sessionMonth || localStorage.getItem("selectedPayrollMonth") || MONTH_NAMES[new Date().getMonth()];
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    const yr = sessionYear ? parseInt(sessionYear, 10) : parseInt(localStorage.getItem("selectedPayrollYear") || String(new Date().getFullYear()), 10);
    return yr || new Date().getFullYear();
  });

  // Reactive sync when Header Navbar Month / Year changes
  useEffect(() => {
    if (sessionMonth && sessionMonth !== selectedMonth) {
      setSelectedMonth(sessionMonth);
      setPreviewData([]);
      setShowPreview(false);
      setImportFile(null);
      setFileVerification({
        isVerified: true,
        fileName: "",
        sheetName: "",
        detectedMonth: null,
        detectedYear: null,
        hasMismatch: false,
        mismatchReason: "",
      });
    }
  }, [sessionMonth]);

  useEffect(() => {
    if (sessionYear) {
      const yr = parseInt(sessionYear, 10);
      if (yr && yr !== selectedYear) {
        setSelectedYear(yr);
        setPreviewData([]);
        setShowPreview(false);
        setImportFile(null);
        setFileVerification({
          isVerified: true,
          fileName: "",
          sheetName: "",
          detectedMonth: null,
          detectedYear: null,
          hasMismatch: false,
          mismatchReason: "",
        });
      }
    }
  }, [sessionYear]);

  // State
  const [existingPayrollList, setExistingPayrollList] = useState([]);
  const [firmEmployees, setFirmEmployees] = useState([]);
  const [activeStaffPayrollList, setActiveStaffPayrollList] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [exportingSaved, setExportingSaved] = useState(false);
  const [exportingActive, setExportingActive] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  // Master parameters for payroll calculations
  const [payrollParams, setPayrollParams] = useState(null);
  const [basicTypeMaster, setBasicTypeMaster] = useState([]);
  const [allowanceMasterList, setAllowanceMasterList] = useState([]);
  const [deductionMasterList, setDeductionMasterList] = useState([]);
  const [pfRate, setPfRate] = useState(null);
  const [esiRate, setEsiRate] = useState(null);
  const [bonusRate, setBonusRate] = useState(null);
  const [gratuityRate, setGratuityRate] = useState(null);

  // Import State
  const [importFile, setImportFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [importedColumns, setImportedColumns] = useState([]); // Detected columns in uploaded file
  const [isParsing, setIsParsing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const [previewFilter, setPreviewFilter] = useState("all"); // all | insert | update | error
  const [searchTerm, setSearchTerm] = useState("");
  
  // Pagination State for Import Preview
  const [importPage, setImportPage] = useState(1);
  const [importPageSize, setImportPageSize] = useState(25);

  // Export Preview State (allows previewing either Saved Payroll or Active Staff Pre-filled)
  const [showExportPreview, setShowExportPreview] = useState(false);
  const [exportPreviewMode, setExportPreviewMode] = useState("saved"); // 'saved' | 'active'
  const [exportSearch, setExportSearch] = useState("");
  const [exportPage, setExportPage] = useState(1);
  const [exportPageSize, setExportPageSize] = useState(25);

  // ── Column Selector & Customization State ──
  const [selectedColumnKeys, setSelectedColumnKeys] = useState(() => {
    try {
      const saved = localStorage.getItem("payroll_export_cols_" + firmId);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (_) {}
    return [];
  });
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [colSearchTerm, setColSearchTerm] = useState("");
  const [activeColCategory, setActiveColCategory] = useState("all");

  // Firm Master List for Cross-Firm Verification
  const [allFirms, setAllFirms] = useState([]);

  useEffect(() => {
    const loadFirms = async () => {
      try {
        const baseUrl = API_WEB_URLS.BASE.endsWith("/") ? API_WEB_URLS.BASE : `${API_WEB_URLS.BASE}/`;
        const res = await fetch(`${baseUrl}${API_WEB_URLS.MASTER}/0/token/FirmMaster/Id/0`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
        if (res.ok) {
          const json = await res.json();
          const list = json?.data?.list || json?.data || [];
          if (Array.isArray(list) && list.length > 0) {
            setAllFirms(list);
          }
        }
      } catch (_) {}
    };
    loadFirms();
  }, [authToken]);

  // Result Modal State
  const [showResultModal, setShowResultModal] = useState(false);
  const [fileVerification, setFileVerification] = useState({
    isVerified: true,
    fileName: "",
    sheetName: "",
    detectedFirm: null,
    activeFirm: selectedFirm?.Name || "",
    detectedMonth: null,
    detectedYear: null,
    hasMismatch: false,
    mismatchReason: "",
    firmMismatch: false,
  });
  const [importResults, setImportResults] = useState({
    total: 0,
    inserted: 0,
    updated: 0,
    failed: 0,
    errors: [],
    details: [],
  });

  // Guidelines Modal State
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);
  const [guidelineSearch, setGuidelineSearch] = useState("");

  const fileInputRef = useRef(null);
  const exportTableRef = useRef(null);
  const importTableRef = useRef(null);

  // ─────────────────────────────────────────────────────────────
  // Load Database-Driven Payroll Data & Masters
  // ─────────────────────────────────────────────────────────────
  const loadExistingData = useCallback(async () => {
    if (!firmId || firmId === "0") return;
    setLoadingData(true);
    try {
      const baseUrl = API_WEB_URLS.BASE.endsWith("/") ? API_WEB_URLS.BASE : `${API_WEB_URLS.BASE}/`;
      const authHeader = authToken ? { Authorization: `Bearer ${authToken}` } : {};

      // Single Database-Driven API Call: Stored Procedure calculates all active & saved payroll data
      const exportUrl = `${baseUrl}PayrollExport/0/token/${firmId}/${selectedMonth}/${selectedYear}`;
      const res = await fetch(exportUrl, { headers: authHeader });

      let savedRecords = [];
      let activeCalculated = [];
      let allowances = [];
      let deductions = [];

      let params = null;
      if (res.ok) {
        const json = await res.json();
        savedRecords = json?.data?.savedRows || json?.data?.rows || [];
        activeCalculated = json?.data?.activeStaffRows || [];
        allowances = json?.data?.allowances || [];
        deductions = json?.data?.deductions || [];
        params = json?.data?.payrollParams || null;
      }

      if (!Array.isArray(savedRecords)) savedRecords = [];
      if (!Array.isArray(activeCalculated)) activeCalculated = [];

      setExistingPayrollList(savedRecords);
      setActiveStaffPayrollList(activeCalculated);
      if (Array.isArray(allowances) && allowances.length > 0) setAllowanceMasterList(allowances);
      if (Array.isArray(deductions) && deductions.length > 0) setDeductionMasterList(deductions);
      if (params) {
        setPayrollParams(params);
      } else {
        // Fallback fetch PayrollParameter
        try {
          const ppRes = await fetch(`${baseUrl}PayrollParameter/0/token/${firmId}`, { headers: authHeader });
          if (ppRes.ok) {
            const ppJson = await ppRes.json();
            const pItem = ppJson?.data?.list?.[0] || ppJson?.data?.[0] || ppJson?.data;
            if (pItem) setPayrollParams(pItem);
          }
        } catch (_) {}
      }

      // Default preview mode to 'active' if no saved records exist yet for this month
      if (savedRecords.length === 0 && activeCalculated.length > 0) {
        setExportPreviewMode("active");
      } else if (savedRecords.length > 0) {
        setExportPreviewMode("saved");
      }

      return {
        savedRecords,
        activeCalculated,
      };
    } catch (e) {
      console.error("Failed to load payroll export data:", e);
      setExistingPayrollList([]);
      setActiveStaffPayrollList([]);
      return {
        savedRecords: [],
        activeCalculated: [],
      };
    } finally {
      setLoadingData(false);
    }
  }, [firmId, selectedMonth, selectedYear, authToken]);

  useEffect(() => {
    loadExistingData();
  }, [loadExistingData]);

  // Dynamic active fields mirroring Payroll Form & Payroll Parameters
  const activePayrollFields = useMemo(() => {
    return getDynamicPayrollFields(payrollParams, allowanceMasterList, deductionMasterList);
  }, [payrollParams, allowanceMasterList, deductionMasterList]);

  // Sync selectedColumnKeys when activePayrollFields loads or changes
  useEffect(() => {
    if (activePayrollFields && activePayrollFields.length > 0) {
      setSelectedColumnKeys((prev) => {
        if (prev && prev.length > 0) {
          const validKeys = prev.filter((k) => activePayrollFields.some((f) => f.key === k));
          return validKeys.length > 0 ? validKeys : activePayrollFields.map((f) => f.key);
        }
        return activePayrollFields.map((f) => f.key);
      });
    }
  }, [activePayrollFields]);

  // Column Selector Handlers
  const handleToggleColumn = (key) => {
    if (key === "EmployeeCode") return; // Locked mandatory key
    setSelectedColumnKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      try {
        localStorage.setItem("payroll_export_cols_" + firmId, JSON.stringify(next));
      } catch (_) {}
      return next;
    });
  };

  const handleSelectAllColumns = () => {
    const allKeys = activePayrollFields.map((f) => f.key);
    setSelectedColumnKeys(allKeys);
    try {
      localStorage.setItem("payroll_export_cols_" + firmId, JSON.stringify(allKeys));
    } catch (_) {}
  };

  const handleDeselectAllColumns = () => {
    const mandatoryKeys = activePayrollFields
      .filter((f) => f.required || f.key === "EmployeeCode" || f.key === "BasicSalary")
      .map((f) => f.key);
    setSelectedColumnKeys(mandatoryKeys);
    try {
      localStorage.setItem("payroll_export_cols_" + firmId, JSON.stringify(mandatoryKeys));
    } catch (_) {}
  };

  const handleToggleCategory = (category) => {
    const categoryFields = activePayrollFields.filter((f) => f.category === category);
    const categoryKeys = categoryFields.map((f) => f.key);
    const allSelected = categoryKeys.every((k) => selectedColumnKeys.includes(k));

    setSelectedColumnKeys((prev) => {
      let next;
      if (allSelected) {
        next = prev.filter((k) => !categoryKeys.includes(k) || k === "EmployeeCode");
      } else {
        const toAdd = categoryKeys.filter((k) => !prev.includes(k));
        next = [...prev, ...toAdd];
      }
      try {
        localStorage.setItem("payroll_export_cols_" + firmId, JSON.stringify(next));
      } catch (_) {}
      return next;
    });
  };

  const handleApplyPreset = (presetType) => {
    let keys = [];
    if (presetType === "all") {
      keys = activePayrollFields.map((f) => f.key);
    } else if (presetType === "standard") {
      keys = activePayrollFields
        .filter((f) =>
          [
            "EmployeeCode",
            "EmployeeName",
            "DepartmentName",
            "DesignationName",
            "BasicSalary",
            "WorkingDays",
            "PaidDay",
            "Wages",
            "GrossAmount",
            "PF",
            "ESI",
            "TotalDeductions",
            "TotalDeduction",
            "NetAmount",
          ].includes(f.key)
        )
        .map((f) => f.key);
    } else if (presetType === "statutory") {
      keys = activePayrollFields
        .filter((f) =>
          [
            "EmployeeCode",
            "EmployeeName",
            "BasicSalary",
            "PaidDay",
            "Wages",
            "GrossAmount",
            "PF",
            "EmplrPF",
            "Pension",
            "ArrearPF",
            "ArrERPF",
            "ArrPens",
            "AdminCharges",
            "AC21",
            "AC22",
            "ESI",
            "EmplrESI",
            "PFApplicable",
            "ESIApplicable",
          ].includes(f.key)
        )
        .map((f) => f.key);
    } else if (presetType === "attendance") {
      keys = activePayrollFields
        .filter(
          (f) =>
            f.category === "Identification" ||
            f.category === "Attendance" ||
            ["BasicSalary", "Wages"].includes(f.key)
        )
        .map((f) => f.key);
    } else if (presetType === "salary") {
      keys = activePayrollFields
        .filter(
          (f) =>
            f.category === "Identification" ||
            f.category === "Salary" ||
            f.category === "Earnings" ||
            f.category === "Summary"
        )
        .map((f) => f.key);
    }

    if (keys.length > 0) {
      if (!keys.includes("EmployeeCode")) keys.unshift("EmployeeCode");
      setSelectedColumnKeys(keys);
      try {
        localStorage.setItem("payroll_export_cols_" + firmId, JSON.stringify(keys));
      } catch (_) {}
    }
  };

  // Sync selected Month/Year
  const handleMonthChange = (m) => {
    setSelectedMonth(m);
    localStorage.setItem("selectedPayrollMonth", m);
    window.dispatchEvent(new Event("sessionChange"));
    window.dispatchEvent(new Event("storage"));
    setPreviewData([]);
    setShowPreview(false);
    setImportFile(null);
    setFileVerification({
      isVerified: true,
      fileName: "",
      sheetName: "",
      detectedMonth: null,
      detectedYear: null,
      hasMismatch: false,
      mismatchReason: "",
    });
  };

  const handleYearChange = (y) => {
    const yr = parseInt(y, 10) || new Date().getFullYear();
    setSelectedYear(yr);
    localStorage.setItem("selectedPayrollYear", String(yr));
    window.dispatchEvent(new Event("sessionChange"));
    window.dispatchEvent(new Event("storage"));
    setPreviewData([]);
    setShowPreview(false);
    setImportFile(null);
    setFileVerification({
      isVerified: true,
      fileName: "",
      sheetName: "",
      detectedMonth: null,
      detectedYear: null,
      hasMismatch: false,
      mismatchReason: "",
    });
  };

  // ─────────────────────────────────────────────────────────────
  // OPTION 1: EXPORT SAVED MONTHLY PAYROLL (.xlsx)
  // ─────────────────────────────────────────────────────────────
  const handleExportSavedPayroll = async () => {
    if (existingPayrollList.length === 0) {
      toast.info(
        `No saved payroll records found for ${selectedMonth} ${selectedYear}. Click "Option 2: Export Active Staff" to download pre-filled payroll data!`,
        { autoClose: 6000 }
      );
      return;
    }
    setExportingSaved(true);
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Payroll System";
      workbook.created = new Date();

      const ws = workbook.addWorksheet(`SavedPayroll_${selectedMonth}_${selectedYear}`, {
        views: [{ showGridLines: true }],
      });

      // Style Header Row using dynamic fields matching PayrollParameter, Firm Masters & Selected Columns
      const allFields = activePayrollFields && activePayrollFields.length > 0 ? activePayrollFields : PAYROLL_FIELDS;
      const selectedSet = new Set(selectedColumnKeys && selectedColumnKeys.length > 0 ? selectedColumnKeys : allFields.map((f) => f.key));
      const filtered = allFields.filter((f) => selectedSet.has(f.key));
      const exportFields = filtered.length > 0 ? filtered : allFields;
      const formattedHeaders = exportFields.map((f) => getFormattedColumnHeader(f));
      const headerRow = ws.addRow(formattedHeaders);
      headerRow.height = 32;
      headerRow.eachCell((cell, colNum) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10, name: "Arial" };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF065F46" }, // Emerald Green header for Saved Records
        };
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: false };
        cell.border = {
          top: { style: "thin", color: { argb: "FF047857" } },
          bottom: { style: "medium", color: { argb: "FF10B981" } },
          left: { style: "thin", color: { argb: "FF047857" } },
          right: { style: "thin", color: { argb: "FF047857" } },
        };

        const fieldDef = exportFields[colNum - 1];
        const formattedHeader = formattedHeaders[colNum - 1] || fieldDef?.label || "";
        ws.getColumn(colNum).width = Math.max(fieldDef?.width || 15, formattedHeader.length + 4);

        if (fieldDef) {
          const noteLines = [
            `📌 ${formattedHeader} [${fieldDef.key}]`,
            fieldDef.type ? `• Type: ${fieldDef.type}` : "",
            fieldDef.required ? "• Required: YES (Mandatory matching key)" : "• Required: Optional",
            fieldDef.hint ? `• Expected: ${fieldDef.hint}` : "",
            fieldDef.example ? `• Example: ${fieldDef.example}` : "",
          ].filter(Boolean);
          cell.note = noteLines.join("\n");
        }
      });

      // Populate with Existing Saved Payroll Transactions
      existingPayrollList.forEach((row, idx) => {
        const rowValues = generateRowValuesWithFormulas(
          row,
          exportFields,
          idx + 2,
          allowanceMasterList,
          deductionMasterList,
          "saved",
          payrollParams
        );

        const r = ws.addRow(rowValues);
        r.height = 20;
        r.eachCell((cell, colNum) => {
          cell.font = { name: "Arial", size: 10 };
          cell.alignment = { vertical: "middle" };
          const fieldDef = exportFields[colNum - 1];
          if (fieldDef?.type === "number") {
            cell.numFmt = "#,##0.00";
            cell.alignment = { vertical: "middle", horizontal: "right" };
          }
        });
      });

      // Add Instructions Sheet
      const infoWs = workbook.addWorksheet("Instructions & Guide");
      infoWs.columns = [
        { header: "Rule / Step", key: "step", width: 28 },
        { header: "Details & Guidelines", key: "details", width: 80 },
      ];
      infoWs.getRow(1).font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
      infoWs.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF065F46" } };

      const instructions = [
        ["1. Saved Records Export", `Contains saved payroll transactions from database for ${selectedMonth} ${selectedYear}.`],
        ["2. Matching Key", "Employee Code [EmployeeCode] or Card No [CardNo] matches existing employee in firm."],
        ["3. Edit & Re-import", "You can update attendance days, overtime hours, advances, or allowances and import this file back to update the database."],
        ["4. Auto Update", "Re-importing will automatically UPDATE existing transactions for this month."],
      ];
      instructions.forEach(([step, details]) => {
        infoWs.addRow({ step, details });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const filename = `Payroll_Saved_${selectedFirm?.Name || "Firm"}_${selectedMonth}_${selectedYear}.xlsx`.replace(/[/\\?%*:|"<>]/g, "-");
      saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), filename);
      toast.success(`Exported ${existingPayrollList.length} saved payroll records successfully!`);
    } catch (e) {
      console.error("Export error:", e);
      toast.error("Failed to export saved payroll data.");
    } finally {
      setExportingSaved(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // OPTION 2: EXPORT ACTIVE EMPLOYEES (PRE-FILLED PAYROLL FORM DATA) (.xlsx)
  // ─────────────────────────────────────────────────────────────
  const handleExportActiveStaffPayroll = async () => {
    let listToExport = activeStaffPayrollList;
    if (!listToExport || listToExport.length === 0) {
      setExportingActive(true);
      const data = await loadExistingData();
      listToExport = data?.activeCalculated || [];
    }

    if (!listToExport || listToExport.length === 0) {
      toast.warning("No active employees found to export for this firm.");
      return;
    }

    setExportingActive(true);
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Payroll System";
      workbook.created = new Date();

      const ws = workbook.addWorksheet(`ActiveStaff_Payroll_${selectedMonth}_${selectedYear}`, {
        views: [{ showGridLines: true }],
      });

      // Style Header Row using dynamic fields matching PayrollParameter, Firm Masters & Selected Columns
      const allFields = activePayrollFields && activePayrollFields.length > 0 ? activePayrollFields : PAYROLL_FIELDS;
      const selectedSet = new Set(selectedColumnKeys && selectedColumnKeys.length > 0 ? selectedColumnKeys : allFields.map((f) => f.key));
      const filtered = allFields.filter((f) => selectedSet.has(f.key));
      const exportFields = filtered.length > 0 ? filtered : allFields;
      const formattedHeaders = exportFields.map((f) => getFormattedColumnHeader(f));
      const headerRow = ws.addRow(formattedHeaders);
      headerRow.height = 32;
      headerRow.eachCell((cell, colNum) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10, name: "Arial" };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF1E3A8A" }, // Royal Blue header for Active Staff
        };
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: false };
        cell.border = {
          top: { style: "thin", color: { argb: "FF1D4ED8" } },
          bottom: { style: "medium", color: { argb: "FF3B82F6" } },
          left: { style: "thin", color: { argb: "FF1D4ED8" } },
          right: { style: "thin", color: { argb: "FF1D4ED8" } },
        };

        const fieldDef = exportFields[colNum - 1];
        const formattedHeader = formattedHeaders[colNum - 1] || fieldDef?.label || "";
        ws.getColumn(colNum).width = Math.max(fieldDef?.width || 15, formattedHeader.length + 4);

        if (fieldDef) {
          const noteLines = [
            `📌 ${formattedHeader} [${fieldDef.key}]`,
            fieldDef.type ? `• Type: ${fieldDef.type}` : "",
            fieldDef.required ? "• Required: YES (Mandatory matching key)" : "• Required: Optional",
            fieldDef.hint ? `• Expected: ${fieldDef.hint}` : "",
            fieldDef.example ? `• Example: ${fieldDef.example}` : "",
          ].filter(Boolean);
          cell.note = noteLines.join("\n");
        }
      });

      // Populate with Active Staff Pre-filled Rows (Exact Payroll Form Data)
      listToExport.forEach((row, idx) => {
        const rowValues = generateRowValuesWithFormulas(
          row,
          exportFields,
          idx + 2,
          allowanceMasterList,
          deductionMasterList,
          "active",
          payrollParams
        );

        const r = ws.addRow(rowValues);
        r.height = 20;
        r.eachCell((cell, colNum) => {
          cell.font = { name: "Arial", size: 10 };
          cell.alignment = { vertical: "middle" };
          const fieldDef = exportFields[colNum - 1];
          if (fieldDef?.type === "number") {
            cell.numFmt = "#,##0.00";
            cell.alignment = { vertical: "middle", horizontal: "right" };
          }
        });
      });

      // Add Instructions Sheet
      const infoWs = workbook.addWorksheet("Instructions & Guide");
      infoWs.columns = [
        { header: "Rule / Step", key: "step", width: 28 },
        { header: "Details & Guidelines", key: "details", width: 80 },
      ];
      infoWs.getRow(1).font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
      infoWs.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E3A8A" } };

      const instructions = [
        ["1. Pre-filled Active Staff File", `Pre-populated with active employee profiles, salaries, attendance calendar days, and allowances for ${selectedMonth} ${selectedYear}.`],
        ["2. Ready for Monthly Adjustments", "Update actual Paid Days, Overtime Hours, Advances, Deductions, or Leaves as per attendance register."],
        ["3. Easy Batch Import", "Once updated, upload this file via the Import card to create or update the month's payroll transactions in one click!"],
        ["4. Statutory Compliance", "PF (12%), EPS Pension (8.33% up to 1250), and ESI (0.75% / 3.25%) are calculated using current rules."],
      ];
      instructions.forEach(([step, details]) => {
        infoWs.addRow({ step, details });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const filename = `Payroll_ActiveStaff_PreFilled_${selectedFirm?.Name || "Firm"}_${selectedMonth}_${selectedYear}.xlsx`.replace(/[/\\?%*:|"<>]/g, "-");
      saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), filename);
      toast.success(`Exported ${activeStaffPayrollList.length} active employees pre-filled payroll data successfully!`);
    } catch (e) {
      console.error("Export active staff error:", e);
      toast.error("Failed to export active staff payroll data.");
    } finally {
      setExportingActive(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // OPTION 3: DOWNLOAD BLANK PAYROLL TEMPLATE (.xlsx)
  // ─────────────────────────────────────────────────────────────
  const handleDownloadBlankTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Payroll System";
      workbook.created = new Date();

      const ws = workbook.addWorksheet(`Payroll_Template_${selectedMonth}_${selectedYear}`, {
        views: [{ showGridLines: true }],
      });

      // Add Header Row using dynamic fields matching PayrollParameter, Firm Masters & Selected Columns
      const allFields = activePayrollFields && activePayrollFields.length > 0 ? activePayrollFields : PAYROLL_FIELDS;
      const selectedSet = new Set(selectedColumnKeys && selectedColumnKeys.length > 0 ? selectedColumnKeys : allFields.map((f) => f.key));
      const filtered = allFields.filter((f) => selectedSet.has(f.key));
      const exportFields = filtered.length > 0 ? filtered : allFields;
      const formattedHeaders = exportFields.map((f) => getFormattedColumnHeader(f));
      const headerRow = ws.addRow(formattedHeaders);
      headerRow.height = 32;
      headerRow.eachCell((cell, colNum) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10, name: "Arial" };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF334155" }, // Slate header for Blank Template
        };
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: false };
        cell.border = {
          top: { style: "thin", color: { argb: "FF475569" } },
          bottom: { style: "medium", color: { argb: "FF64748B" } },
          left: { style: "thin", color: { argb: "FF475569" } },
          right: { style: "thin", color: { argb: "FF475569" } },
        };

        const fieldDef = exportFields[colNum - 1];
        const formattedHeader = formattedHeaders[colNum - 1] || fieldDef?.label || "";
        ws.getColumn(colNum).width = Math.max(fieldDef?.width || 15, formattedHeader.length + 4);

        if (fieldDef) {
          const noteLines = [
            `📌 ${formattedHeader} [${fieldDef.key}]`,
            fieldDef.type ? `• Type: ${fieldDef.type}` : "",
            fieldDef.required ? "• Required: YES (Mandatory matching key)" : "• Required: Optional",
            fieldDef.hint ? `• Expected: ${fieldDef.hint}` : "",
            fieldDef.example ? `• Example: ${fieldDef.example}` : "",
          ].filter(Boolean);
          cell.note = noteLines.join("\n");
        }
      });

      // Sample Guidance Row
      const sampleRowValues = generateRowValuesWithFormulas(
        {},
        exportFields,
        2,
        allowanceMasterList,
        deductionMasterList,
        "blank",
        payrollParams
      );
      const r = ws.addRow(sampleRowValues);
      r.height = 20;
      r.eachCell((cell, colNum) => {
        cell.font = { name: "Arial", size: 10, italic: true, color: { argb: "FF64748B" } };
        cell.alignment = { vertical: "middle" };
        const fieldDef = exportFields[colNum - 1];
        if (fieldDef?.type === "number") {
          cell.numFmt = "#,##0.00";
          cell.alignment = { vertical: "middle", horizontal: "right" };
        }
      });

      // Add Instructions Sheet
      const infoWs = workbook.addWorksheet("Instructions & Guide");
      infoWs.columns = [
        { header: "Rule / Step", key: "step", width: 28 },
        { header: "Details & Guidelines", key: "details", width: 80 },
      ];
      infoWs.getRow(1).font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
      infoWs.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E293B" } };

      const instructions = [
        ["1. Blank Template", "Use this template for manual payroll entry or bulk importing new employee transactions."],
        ["2. Employee Code", "Employee Code [EmployeeCode] must match an active employee in this firm."],
        ["3. Sample Row", "Row 2 contains sample guidance values and can be replaced or deleted."],
      ];
      instructions.forEach(([step, details]) => {
        infoWs.addRow({ step, details });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const filename = `Payroll_Blank_Template_${selectedFirm?.Name || "Firm"}_${selectedMonth}_${selectedYear}.xlsx`.replace(/[/\\?%*:|"<>]/g, "-");
      saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), filename);
      toast.success("Blank payroll template downloaded!");
    } catch (e) {
      console.error("Template error:", e);
      toast.error("Failed to download blank template.");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // 3. EXCEL FILE PARSER & CLIENT-SIDE VALIDATOR (With Disambiguated Matching)
  // ─────────────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setIsParsing(true);
    setPreviewData([]);
    setShowPreview(false);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);

      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        toast.error("The uploaded Excel file has no worksheets.");
        setIsParsing(false);
        return;
      }

      // ─── Double Verification Engine (FileName + Tab/Sheet Name) ───
      const rawFileName = file.name || "";
      const rawSheetName = worksheet.name || "";

      const SHORT_MONTHS_MAP = {
        jan: "January",
        feb: "February",
        mar: "March",
        apr: "April",
        may: "May",
        jun: "June",
        jul: "July",
        aug: "August",
        sep: "September",
        sept: "September",
        oct: "October",
        nov: "November",
        dec: "December",
      };

      const extractMonthAndYear = (text) => {
        if (!text) return { month: null, year: null };
        let detectedMonth = null;
        let detectedYear = null;

        const cleanText = text.replace(/\.xlsx?$/i, "").trim();

        // 1. Check Full Month Names (case-insensitive, separated by non-alpha or boundary)
        for (const m of MONTH_NAMES) {
          const regex = new RegExp(`(?:^|[^a-zA-Z])${m}(?:[^a-zA-Z]|$)`, "i");
          if (regex.test(cleanText)) {
            detectedMonth = m;
            break;
          }
        }

        // 2. Check 3-4 letter Month abbreviations (e.g. Jun, Feb, Sept)
        if (!detectedMonth) {
          for (const [sm, fullM] of Object.entries(SHORT_MONTHS_MAP)) {
            const regex = new RegExp(`(?:^|[^a-zA-Z])${sm}(?:[^a-zA-Z]|$)`, "i");
            if (regex.test(cleanText)) {
              detectedMonth = fullM;
              break;
            }
          }
        }

        // 3. Check 4-digit Year (2020-2035)
        const yearMatch = cleanText.match(/(?:^|[^0-9])(20\d\d)(?:[^0-9]|$)/);
        if (yearMatch) {
          detectedYear = parseInt(yearMatch[1], 10);
        }

        // 4. Check Numeric patterns like MM-YYYY or YYYY-MM or MM_YYYY
        if (!detectedMonth || !detectedYear) {
          const mmyyyy = cleanText.match(/(?:^|[^0-9])(0?[1-9]|1[0-2])[-_/.](20\d\d)(?:[^0-9]|$)/);
          if (mmyyyy) {
            const mNum = parseInt(mmyyyy[1], 10);
            if (!detectedMonth && mNum >= 1 && mNum <= 12) detectedMonth = MONTH_NAMES[mNum - 1];
            if (!detectedYear) detectedYear = parseInt(mmyyyy[2], 10);
          }
          const yyyymm = cleanText.match(/(?:^|[^0-9])(20\d\d)[-_/.](0?[1-9]|1[0-2])(?:[^0-9]|$)/);
          if (yyyymm) {
            const mNum = parseInt(yyyymm[2], 10);
            if (!detectedMonth && mNum >= 1 && mNum <= 12) detectedMonth = MONTH_NAMES[mNum - 1];
            if (!detectedYear) detectedYear = parseInt(yyyymm[1], 10);
          }
        }

        return { month: detectedMonth, year: detectedYear };
      };

      // ─── Extract Firm Name from File / Tab / Sheet Header Cells ───
      const normalizeFirmText = (s) =>
        (s || "")
          .toLowerCase()
          .replace(/payroll/gi, "")
          .replace(/activestaff/gi, "")
          .replace(/prefilled/gi, "")
          .replace(/template/gi, "")
          .replace(/saved/gi, "")
          .replace(/salary/gi, "")
          .replace(/attendance/gi, "")
          .replace(/[^a-z0-9]/g, "")
          .trim();

      const extractFirm = (text) => {
        if (!text) return null;
        const cleanText = normalizeFirmText(text);
        if (!cleanText || cleanText.length < 2) return null;

        // 1. Check if active selected firm name is in the text
        if (selectedFirm?.Name) {
          const normActive = normalizeFirmText(selectedFirm.Name);
          if (normActive.length >= 3 && cleanText.includes(normActive)) {
            return selectedFirm.Name;
          }
        }

        // 2. Check if any known firm from allFirms is in the text
        if (Array.isArray(allFirms) && allFirms.length > 0) {
          for (const f of allFirms) {
            const fName = f.Name || f.FirmName || "";
            const normF = normalizeFirmText(fName);
            if (normF.length >= 3 && cleanText.includes(normF)) {
              return fName;
            }
          }
        }
        return null;
      };

      // Also check top 3 rows of worksheet for company / firm name headers
      let headerCellFirm = null;
      try {
        for (let r = 1; r <= Math.min(worksheet.rowCount || 3, 3); r++) {
          const rowObj = worksheet.getRow(r);
          if (rowObj) {
            rowObj.eachCell((c) => {
              const cellVal = String(c.value || "").trim();
              if (cellVal && !headerCellFirm) {
                const found = extractFirm(cellVal);
                if (found) headerCellFirm = found;
              }
            });
          }
          if (headerCellFirm) break;
        }
      } catch (_) {}

      const fileFirm = extractFirm(rawFileName);
      const sheetFirm = extractFirm(rawSheetName);
      const detectedFirm = fileFirm || sheetFirm || headerCellFirm;

      const norm = (s) => normalizeFirmText(s);
      const firmMismatch = Boolean(
        detectedFirm &&
        selectedFirm?.Name &&
        norm(detectedFirm) !== norm(selectedFirm.Name)
      );

      const fileInfo = extractMonthAndYear(rawFileName);
      const sheetInfo = extractMonthAndYear(rawSheetName);

      const detectedMonth = fileInfo.month || sheetInfo.month;
      const detectedYear = fileInfo.year || sheetInfo.year;

      const fileMonthMismatch = Boolean(fileInfo.month && fileInfo.month.toLowerCase() !== selectedMonth.toLowerCase());
      const sheetMonthMismatch = Boolean(sheetInfo.month && sheetInfo.month.toLowerCase() !== selectedMonth.toLowerCase());

      const fileYearMismatch = Boolean(fileInfo.year && Number(fileInfo.year) !== Number(selectedYear));
      const sheetYearMismatch = Boolean(sheetInfo.year && Number(sheetInfo.year) !== Number(selectedYear));

      let hasMismatch = false;
      let mismatchReasons = [];

      if (firmMismatch) {
        hasMismatch = true;
        mismatchReasons.push(`Firm mismatch: File specifies Firm "${detectedFirm}", but active target session firm is "${selectedFirm?.Name}".`);
      }
      if (fileMonthMismatch) {
        hasMismatch = true;
        mismatchReasons.push(`File Name specifies month "${fileInfo.month}", but active target session is "${selectedMonth}".`);
      }
      if (sheetMonthMismatch && sheetInfo.month?.toLowerCase() !== fileInfo.month?.toLowerCase()) {
        hasMismatch = true;
        mismatchReasons.push(`Excel Sheet/Tab specifies month "${sheetInfo.month}", but active target session is "${selectedMonth}".`);
      }
      if (fileYearMismatch) {
        hasMismatch = true;
        mismatchReasons.push(`File Name specifies year "${fileInfo.year}", but active target session is "${selectedYear}".`);
      }
      if (sheetYearMismatch && sheetInfo.year !== fileInfo.year) {
        hasMismatch = true;
        mismatchReasons.push(`Excel Sheet/Tab specifies year "${sheetInfo.year}", but active target session is "${selectedYear}".`);
      }

      const mismatchReason = mismatchReasons.join(" ");

      const verificationState = {
        isVerified: !hasMismatch,
        fileName: rawFileName,
        sheetName: rawSheetName,
        detectedFirm,
        activeFirm: selectedFirm?.Name || "",
        firmMismatch,
        detectedMonth,
        detectedYear,
        fileMonth: fileInfo.month,
        fileYear: fileInfo.year,
        sheetMonth: sheetInfo.month,
        sheetYear: sheetInfo.year,
        hasMismatch,
        mismatchReason,
      };

      setFileVerification(verificationState);

      if (hasMismatch) {
        toast.error(`⚠️ Triple Verification Warning: ${mismatchReason}`, { autoClose: 9000 });
      } else if (detectedFirm || fileInfo.month || sheetInfo.month || fileInfo.year || sheetInfo.year) {
        toast.success(`✅ Triple Verification Passed: Verified for ${selectedFirm?.Name || "Firm"} - ${selectedMonth} ${selectedYear}`);
      }

      // Read Header Row & Map to Canonical Keys
      const headerRow = worksheet.getRow(1);
      const colMap = {}; // colNumber -> fieldKey
      const detectedCols = [];

      const allFields = activePayrollFields && activePayrollFields.length > 0 ? activePayrollFields : PAYROLL_FIELDS;

      headerRow.eachCell((cell, colNum) => {
        const rawHeader = String(cell.value || "").trim();
        if (!rawHeader) return;

        // 1. Check if key is explicitly inside brackets e.g. [PF], [EmplrPF], (PF), (EmplrPF), [ESI], [EmplrESI]
        let matchedKey = null;

        const squareMatch = rawHeader.match(/\[([A-Za-z0-9_]+)\]/);
        if (squareMatch) {
          const inner = squareMatch[1].trim();
          if (allFields.some((f) => f.key.toLowerCase() === inner.toLowerCase())) {
            matchedKey = allFields.find((f) => f.key.toLowerCase() === inner.toLowerCase())?.key;
          }
        }

        if (!matchedKey) {
          const parenMatch = rawHeader.match(/\(([A-Za-z0-9_]+)\)/);
          if (parenMatch) {
            const inner = parenMatch[1].trim();
            if (allFields.some((f) => f.key.toLowerCase() === inner.toLowerCase())) {
              matchedKey = allFields.find((f) => f.key.toLowerCase() === inner.toLowerCase())?.key;
            }
          }
        }

        // 2. Exact match on raw header / clean header against canonical aliases
        if (!matchedKey) {
          const cleanHeader = rawHeader.replace(/\s*\([^)]*\)/g, "").replace(/\s*\[[^\]]*\]/g, "").trim();
          const normRaw = rawHeader.toLowerCase().replace(/[_\s]+/g, " ").trim();
          const normClean = cleanHeader.toLowerCase().replace(/[_\s]+/g, " ").trim();
          const compactClean = cleanHeader.replace(/[\s_]+/g, "").toLowerCase();

          if (PAYROLL_HEADER_ALIASES[normRaw]) {
            matchedKey = PAYROLL_HEADER_ALIASES[normRaw];
          } else if (PAYROLL_HEADER_ALIASES[normClean]) {
            matchedKey = PAYROLL_HEADER_ALIASES[normClean];
          } else if (PAYROLL_HEADER_ALIASES[compactClean]) {
            matchedKey = PAYROLL_HEADER_ALIASES[compactClean];
          }
        }

        // 3. Match against allFields labels
        if (!matchedKey) {
          const cleanHeader = rawHeader.replace(/\s*\([^)]*\)/g, "").replace(/\s*\[[^\]]*\]/g, "").trim().toLowerCase();
          for (const f of allFields) {
            const fLabelClean = f.label.replace(/\s*\([^)]*\)/g, "").trim().toLowerCase();
            if (cleanHeader === fLabelClean || cleanHeader === f.key.toLowerCase()) {
              matchedKey = f.key;
              break;
            }
          }
        }

        // Fallback: use raw header or sanitized text
        const finalKey = matchedKey || rawHeader.replace(/\s+/g, "");
        colMap[colNum] = finalKey;

        const fieldDef = allFields.find((f) => f.key === finalKey);
        detectedCols.push({
          key: finalKey,
          label: fieldDef ? fieldDef.label : rawHeader,
          type: fieldDef?.type || "string",
          colNum: colNum,
        });
      });

      setImportedColumns(detectedCols);

      // Ensure active employees are loaded before matching
      let currentFirmEmployees = firmEmployees;
      if (currentFirmEmployees.length === 0 && firmId > 0) {
        try {
          const empRes = await fetch(`${API_WEB_URLS.BASE}EmployeeExport/0/token/${firmId}`, {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
          });
          if (empRes.ok) {
            const empJson = await empRes.json();
            const emps = empJson?.data?.rows || empJson?.data?.dataList || empJson?.data || [];
            if (Array.isArray(emps) && emps.length > 0) {
              currentFirmEmployees = emps;
              setFirmEmployees(emps);
            }
          }
        } catch (err) {
          console.warn("Could not fetch active employees during file parsing:", err);
        }
      }

      // Pre-build robust multi-index employee lookup maps
      const empByExactCode = new Map(); // exact string matching (code, cardno, serialno, id)
      const empByNumeric = new Map();   // numeric string matching without leading zeros (e.g. "0005" -> "5")
      const empByName = new Map();      // exact lowercase name
      const empByCleanName = new Map(); // clean alphanumeric name
      const empByNameAndFather = new Map(); // name + father name composite key

      (currentFirmEmployees || []).forEach((e) => {
        const idStr = String(e.Id || "").trim().toLowerCase();
        const codeStr = String(e.EmployeeCode || "").trim().toLowerCase();
        const cardStr = String(e.CardNo || "").trim().toLowerCase();
        const serialStr = String(e.SerialNo || "").trim().toLowerCase();
        const nameStr = String(e.Name || "").trim().toLowerCase();
        const cleanNameStr = nameStr.replace(/[^a-z0-9]/g, "");
        const fatherStr = String(e.FatherName || "").trim().toLowerCase();
        const cleanFatherStr = fatherStr.replace(/[^a-z0-9]/g, "");

        [codeStr, cardStr, serialStr, idStr].forEach((key) => {
          if (key) {
            if (!empByExactCode.has(key)) empByExactCode.set(key, e);
            const parsed = parseInt(key, 10);
            if (!isNaN(parsed) && parsed > 0) {
              const numKey = String(parsed);
              if (!empByNumeric.has(numKey)) empByNumeric.set(numKey, e);
            }
          }
        });

        if (nameStr && !empByName.has(nameStr)) {
          empByName.set(nameStr, e);
        }
        if (cleanNameStr && !empByCleanName.has(cleanNameStr)) {
          empByCleanName.set(cleanNameStr, e);
        }
        if (cleanNameStr && cleanFatherStr) {
          const nfKey = `${cleanNameStr}__${cleanFatherStr}`;
          if (!empByNameAndFather.has(nfKey)) {
            empByNameAndFather.set(nfKey, e);
          }
        }
      });

      const findMatchedEmployee = (code, name, father) => {
        const rawCode = String(code || "").trim();
        const lowerCode = rawCode.toLowerCase();
        const rawName = String(name || "").trim();
        const lowerName = rawName.toLowerCase();
        const cleanName = lowerName.replace(/[^a-z0-9]/g, "");
        const rawFather = String(father || "").trim();
        const cleanFather = rawFather.toLowerCase().replace(/[^a-z0-9]/g, "");

        // 1. Priority match by Name + Father Name (if father is available)
        if (cleanName && cleanFather) {
          const nfKey = `${cleanName}__${cleanFather}`;
          if (empByNameAndFather.has(nfKey)) {
            return empByNameAndFather.get(nfKey);
          }
        }

        // 2. Check exact string code/card/serial
        if (lowerCode && empByExactCode.has(lowerCode)) {
          return empByExactCode.get(lowerCode);
        }

        // 3. Check numeric match (stripping leading zeros)
        if (rawCode) {
          const parsed = parseInt(rawCode, 10);
          if (!isNaN(parsed) && parsed > 0 && empByNumeric.has(String(parsed))) {
            return empByNumeric.get(String(parsed));
          }
        }

        // 4. Check exact name match
        if (lowerName && empByName.has(lowerName)) {
          return empByName.get(lowerName);
        }

        // 5. Check clean alphanumeric name match
        if (cleanName && empByCleanName.has(cleanName)) {
          return empByCleanName.get(cleanName);
        }

        return null;
      };

      const existingByEmpId = new Map();
      const existingByCode = new Map();
      (existingPayrollList || []).forEach((p) => {
        const id = Number(p.F_EmployeeMaster || p.EmployeeId || 0);
        if (id > 0) existingByEmpId.set(id, p);
        const c = String(p.EmployeeCode || p.CardNo || "").trim().toLowerCase();
        if (c) existingByCode.set(c, p);
      });

      const rows = [];
      const rowCount = worksheet.rowCount;
      const seenEmpCodes = new Map(); // Tracking duplicate employees in uploaded sheet

      // Calendar days in selected month and year
      const monthIdx = MONTH_NAMES.indexOf(selectedMonth);
      const daysInMonth = new Date(selectedYear, monthIdx >= 0 ? monthIdx + 1 : 1, 0).getDate() || 30;

      for (let r = 2; r <= rowCount; r++) {
        const row = worksheet.getRow(r);
        const isEmpty = !row.values || row.values.every((v) => v === undefined || v === null || v === "");
        if (isEmpty) continue;

        const rowData = { _sNo: rows.length + 1, _rowIndex: r };
        Object.entries(colMap).forEach(([colNum, key]) => {
          const cell = row.getCell(Number(colNum));
          let cellVal = cell.value;
          if (cell && cell.result !== undefined && cell.result !== null) {
            cellVal = cell.result;
          } else if (cellVal && typeof cellVal === "object") {
            if (cellVal.result !== undefined && cellVal.result !== null) {
              cellVal = cellVal.result;
            } else if (cellVal.text !== undefined && cellVal.text !== null) {
              cellVal = cellVal.text;
            } else if (cellVal.formula !== undefined) {
              cellVal = 0;
            }
          }
          rowData[key] = cellVal;
        });

        // Skip total row if present
        const testCode = String(rowData.EmployeeCode || rowData.CardNo || "").trim().toUpperCase();
        if (testCode === "TOTAL" || testCode === "GRAND TOTAL") continue;

        const errors = [];

        // 1. Employee Identification
        const empCode = String(rowData.EmployeeCode || rowData.CardNo || "").trim();
        const empName = String(rowData.EmployeeName || rowData.Name || "").trim();
        const empFather = String(rowData.FatherName || rowData.Father || "").trim();

        if (!empCode && !empName) {
          errors.push("Employee Code or Name is required");
        }

        // 2. Duplicate Employee in uploaded file check (matching Payroll & MultiPayroll rule)
        const dupKey = (empCode || (empFather ? `${empName}__${empFather}` : empName)).toLowerCase();
        if (dupKey) {
          if (seenEmpCodes.has(dupKey)) {
            errors.push(`Duplicate employee (${empCode || empName}) already present in Excel row ${seenEmpCodes.get(dupKey)}`);
          } else {
            seenEmpCodes.set(dupKey, r);
          }
        }

        // 3. Match against existing DB employees using multi-index lookup (with Name + FatherName priority)
        const matchedEmp = findMatchedEmployee(empCode, empName, empFather);

        if (!matchedEmp && (empCode || empName)) {
          errors.push(`Employee not found in active firm (${empCode || empName})`);
        }

        // Check existing payroll transaction
        let existingRecord = null;
        if (matchedEmp && existingByEmpId.has(Number(matchedEmp.Id))) {
          existingRecord = existingByEmpId.get(Number(matchedEmp.Id));
        } else if (empCode && existingByCode.has(empCode.toLowerCase())) {
          existingRecord = existingByCode.get(empCode.toLowerCase());
        }

        // 4. Numerical sanitization
        allFields.forEach((f) => {
          if (f.type === "number") {
            const raw = rowData[f.key];
            if (raw !== undefined && raw !== null && raw !== "") {
              const num = parseNum(raw);
              if (isNaN(num)) {
                errors.push(`${f.label} must be numeric`);
              } else if (num < 0) {
                errors.push(`${f.label} cannot be negative`);
              } else {
                rowData[f.key] = num;
              }
            } else {
              rowData[f.key] = 0;
            }
          }
        });

        // 5. Basic Salary Validation (like in Payroll - cannot be 0 or negative)
        let basicSal = parseNum(rowData.BasicSalary);
        if (basicSal <= 0 && matchedEmp) {
          // Fallback to employee profile salary if absent/zero in excel
          const masterBasic = parseNum(matchedEmp.BasicSalary || matchedEmp.Sal_BasicSalary || matchedEmp.Basic || matchedEmp.RatePerDay || matchedEmp.Salary || 0);
          if (masterBasic > 0) {
            basicSal = masterBasic;
            rowData.BasicSalary = masterBasic;
          }
        }
        if (basicSal <= 0) {
          errors.push("Basic Salary cannot be 0 or empty (must be greater than 0)");
        }

        // 6. Month Days Validation (must be a valid month day count between 1 and 31)
        let monthDays = parseNum(rowData.MonthDays);
        if (!monthDays || monthDays <= 0) {
          monthDays = daysInMonth;
          rowData.MonthDays = daysInMonth;
        } else if (monthDays < 1 || monthDays > 31) {
          errors.push(`Month Days (${monthDays}) must be between 1 and 31`);
        }

        // 7. Working Days & Attendance Days Validation (cannot exceed Month Days)
        const workingDays = parseNum(rowData.WorkingDays);
        if (workingDays > monthDays) {
          errors.push(`Working Days (${workingDays}) cannot exceed Month Days (${monthDays})`);
        }


        // 8. Paid Days Validation & Auto-Capping (Matching Payroll & MultiPayrollEntry rules)
        let paidDays = parseNum(rowData.PaidDay);
        const leaveWages = parseNum(rowData.LeaveWages);
        const festivalHol = parseNum(rowData.FestivalHoliday);
        const weeklyHol = parseNum(rowData.WeeklyHoliday);
        const clDays = parseNum(rowData.CL);
        const mlDays = parseNum(rowData.ML);
        const totalAttendance = workingDays + leaveWages + festivalHol + weeklyHol + clDays + mlDays;

        
        if (paidDays <= 0 && totalAttendance > 0) {
          paidDays = Math.min(monthDays, totalAttendance);
          rowData.PaidDay = paidDays;
        } else if (paidDays > monthDays) {
          // If paid days is greater than month days, auto-cap to month days
          paidDays = monthDays;
          rowData.PaidDay = paidDays;
        } else if (!paidDays && workingDays === 0) {
          paidDays = Math.min(monthDays, totalAttendance);
          rowData.PaidDay = paidDays;
        }

        // 9. Working Hours & Minutes Validation
        const workingMins = parseNum(rowData.WorkingMinutes);
        if (workingMins < 0 || workingMins >= 60) {
          errors.push(`Working Minutes (${workingMins}) must be between 0 and 59`);
        }

        // 10. Overtime Hours Validation
        const otHours = parseNum(rowData.OvertimeHours);
        if (otHours > 200) {
          errors.push(`Overtime Hours (${otHours}) exceeds maximum allowed (200 hrs)`);
        }
        
        // 11. Auto-calculate Wages & Gross if 0
        if (!rowData.Wages || rowData.Wages === 0) {
          const effectivePaidDays = paidDays > 0 ? paidDays : workingDays > 0 ? workingDays : monthDays;
          const basicType = Number(matchedEmp?.F_BasicTypeMaster || matchedEmp?.BasicType || 1);
          if (basicType === 2) {
            // Daily
            rowData.Wages = Math.round((basicSal * effectivePaidDays) * 100) / 100;
          } else if (basicType === 3) {
            // Hourly: (PaidDays * stdHours) + extraHours
            const stdWH = parseFloat(payrollParams?.WorkingHours) || 8;
            const extraHours = parseNum(rowData.WorkingHours) + (parseNum(rowData.WorkingMinutes) / 60);
            const totalHours = (effectivePaidDays * stdWH) + extraHours;
            rowData.Wages = Math.round((basicSal * totalHours) * 100) / 100;
          } else {
            // Monthly
            rowData.Wages = Math.round((basicSal * effectivePaidDays / monthDays) * 100) / 100;
          }
        }

        // Auto-calculate OverTime if 0 but OvertimeHours provided
        if ((!rowData.OverTime || rowData.OverTime === 0) && otHours > 0) {
          const isOTOn26 = !!(
            payrollParams?.IsOTOn26Days ||
            payrollParams?.OvertimeOn26Days ||
            payrollParams?.IsOvertimeOn26Days ||
            String(payrollParams?.IsOTOn26Days).toLowerCase() === "true" ||
            String(payrollParams?.OvertimeOn26Days).toLowerCase() === "true"
          );
          const isDoubleOT = !!(
            payrollParams?.IsDoubleOT ||
            payrollParams?.DoubleOvertimeAmount ||
            payrollParams?.IsDoubleOvertimeAmount ||
            String(payrollParams?.IsDoubleOT).toLowerCase() === "true"
          );
          const stdWH = parseFloat(payrollParams?.WorkingHours) || 8;
          const mult = isDoubleOT ? 2 : 1;
          const basicType = Number(matchedEmp?.F_BasicTypeMaster || matchedEmp?.BasicType || 1);
          let hourlyRate = 0;
          if (basicType === 2) {
            hourlyRate = basicSal / stdWH;
          } else if (basicType === 3) {
            hourlyRate = basicSal;
          } else {
            const divisor = isOTOn26 ? 26 : (monthDays || 26);
            hourlyRate = basicSal / divisor / stdWH;
          }
          rowData.OverTime = Math.round(hourlyRate * mult * otHours * 100) / 100;
        }

        const totalEarnings = (rowData.Wages || basicSal) + parseNum(rowData.TotalAllowances) + parseNum(rowData.OverTime) + parseNum(rowData.Arrear) + parseNum(rowData.AdvanceBonus) + parseNum(rowData.ExtraPay) + parseNum(rowData.ExtraPay1) + parseNum(rowData.Bonus);
        if (!rowData.GrossAmount || rowData.GrossAmount === 0) {
          rowData.GrossAmount = Math.round(totalEarnings * 100) / 100;
        }

        // 12. Statutory PF & ESI Validation
        const pfAmt = parseNum(rowData.PF);
        if (pfAmt > rowData.GrossAmount && rowData.GrossAmount > 0) {
          errors.push(`Employee PF (${pfAmt}) cannot exceed Gross Amount (${rowData.GrossAmount})`);
        }

        const esiAmt = parseNum(rowData.ESI);
        if (esiAmt > rowData.GrossAmount && rowData.GrossAmount > 0) {
          errors.push(`Employee ESI (${esiAmt}) cannot exceed Gross Amount (${rowData.GrossAmount})`);
        }

        // 13. Total Deductions Validation
        const computedDeductions = pfAmt + esiAmt + parseNum(rowData.TDS) + parseNum(rowData.Advance) + parseNum(rowData.Recovery) + parseNum(rowData.ArrearPF) + parseNum(rowData.TotalDeductions);
        if (!rowData.TotalDeduction || rowData.TotalDeduction === 0) {
          rowData.TotalDeduction = Math.round(computedDeductions * 100) / 100;
        }

        if (rowData.TotalDeduction > rowData.GrossAmount) {
          errors.push(`Total Deductions (${rowData.TotalDeduction}) cannot exceed Gross Amount (${rowData.GrossAmount})`);
        }

        // 14. Net Amount Validation
        const computedNet = Math.round((rowData.GrossAmount - rowData.TotalDeduction + parseNum(rowData.PFRefund)) * 100) / 100;
        if (!rowData.NetAmount || rowData.NetAmount === 0) {
          rowData.NetAmount = Math.max(0, computedNet);
        }
        if (computedNet < 0) {
          errors.push(`Net Payable Amount cannot be negative (${computedNet})`);
        }

        const status = errors.length > 0 ? "error" : "valid";
        const type = errors.length > 0 ? "INVALID" : existingRecord ? "UPDATE" : "INSERT";

        rows.push({
          ...rowData,
          EmployeeCode: empCode || matchedEmp?.EmployeeCode || matchedEmp?.CardNo || "",
          CardNo: empCode || matchedEmp?.EmployeeCode || matchedEmp?.CardNo || "",
          EmployeeName: empName || matchedEmp?.Name || "",
          DepartmentName: rowData.DepartmentName || matchedEmp?.DepartmentName || "",
          DesignationName: rowData.DesignationName || matchedEmp?.DesignationName || "",
          F_EmployeeMaster: matchedEmp?.Id || 0,
          status,
          type,
          errors,
          existingRecord,
        });
      }

      setPreviewData(rows);
      setShowPreview(true);
      setImportPage(1);

      const validCount = rows.filter((r) => r.status === "valid").length;
      const errorCount = rows.filter((r) => r.status === "error").length;
      if (errorCount > 0) {
        toast.warning(`⚠️ Parsed ${rows.length} rows: ${validCount} valid, ${errorCount} invalid rows with errors.`);
      } else {
        toast.success(`✅ Parsed ${rows.length} rows: All records valid and ready to import!`);
      }
    } catch (e) {
      console.error("Parse error:", e);
      toast.error("Failed to parse Excel file. Ensure it is a valid .xlsx document.");
    } finally {
      setIsParsing(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // 4. SUBMIT BATCH PAYROLL IMPORT TO API
  // ─────────────────────────────────────────────────────────────
  const handleImportSubmit = async () => {
    const validRows = previewData.filter((r) => r.status === "valid");
    if (validRows.length === 0) {
      toast.warning("No valid rows to import.");
      return;
    }

    if (fileVerification?.hasMismatch) {
      const blockTitle = fileVerification.firmMismatch
        ? "⛔ IMPORT BLOCKED: Firm Mismatch!"
        : "⛔ IMPORT BLOCKED: Month / Year Period Mismatch!";

      toast.error(
        `${blockTitle}\n\n${fileVerification.mismatchReason}\n\nPlease switch to the correct Firm / Month in the top header session, or upload the file matching "${selectedFirm?.Name || "Firm"}" - "${selectedMonth} ${selectedYear}".`,
        { autoClose: 9000 }
      );
      return;
    }

    setImporting(true);
    setImportProgress(10);

    try {
      // Build clean JSON payload for Stored Procedure
      const allFields = activePayrollFields && activePayrollFields.length > 0 ? activePayrollFields : PAYROLL_FIELDS;
      const payload = validRows.map((r) => {
        const item = {
          CardNo: r.CardNo || r.EmployeeCode,
          EmployeeCode: r.EmployeeCode || r.CardNo,
          EmployeeName: r.EmployeeName,
          F_EmployeeMaster: r.F_EmployeeMaster || 0,
          PayrollMonth: selectedMonth,
          PayrollYear: selectedYear,
        };
        allFields.forEach((f) => {
          if (f.key !== "CardNo" && f.key !== "EmployeeName") {
            item[f.key] = r[f.key] !== undefined ? r[f.key] : 0;
          }
        });
        return item;
      });

      setImportProgress(40);

      const importUrl = `${API_WEB_URLS.BASE}PayrollImport/${userId}/token/${firmId}?month=${encodeURIComponent(selectedMonth)}&year=${selectedYear}`;
      const res = await fetch(importUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      setImportProgress(80);

      const json = await res.json();
      setImportProgress(100);

      if (res.ok && json.success) {
        const summary = json?.data?.summary || {};
        const results = json?.data?.results || [];

        setImportResults({
          total: validRows.length,
          inserted: summary.InsertedCount || results.filter((x) => x.Action === "INSERTED").length,
          updated: summary.UpdatedCount || results.filter((x) => x.Action === "UPDATED").length,
          failed: summary.FailedCount || results.filter((x) => x.Status === "FAILED").length,
          details: results,
          errors: results.filter((x) => x.Status === "FAILED"),
        });

        setShowResultModal(true);
        toast.success("Payroll import completed successfully!");
        loadExistingData();
      } else {
        toast.error(json.message || "Import failed on server.");
      }
    } catch (e) {
      console.error("Import error:", e);
      toast.error("Network error during payroll import.");
    } finally {
      setImporting(false);
    }
  };

  // Reset import
  const resetImport = () => {
    setImportFile(null);
    setPreviewData([]);
    setShowPreview(false);
    setImportPage(1);
    setFileVerification({
      isVerified: true,
      fileName: "",
      sheetName: "",
      detectedMonth: null,
      detectedYear: null,
      hasMismatch: false,
      mismatchReason: "",
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Filtered & Paginated Preview Data
  const filteredPreview = useMemo(() => {
    return previewData.filter((r) => {
      if (previewFilter === "insert" && r.type !== "INSERT") return false;
      if (previewFilter === "update" && r.type !== "UPDATE") return false;
      if (previewFilter === "error" && r.status !== "error") return false;
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        const code = String(r.CardNo || r.EmployeeCode || "").toLowerCase();
        const name = String(r.EmployeeName || "").toLowerCase();
        const dept = String(r.DepartmentName || "").toLowerCase();
        return code.includes(s) || name.includes(s) || dept.includes(s);
      }
      return true;
    });
  }, [previewData, previewFilter, searchTerm]);

  const totalImportPages = Math.ceil(filteredPreview.length / importPageSize) || 1;
  const currentImportPage = Math.min(importPage, totalImportPages);
  const paginatedImportRows = filteredPreview.slice((currentImportPage - 1) * importPageSize, currentImportPage * importPageSize);

  const validCount = previewData.filter((r) => r.status === "valid").length;
  const errorCount = previewData.filter((r) => r.status === "error").length;
  const insertCount = previewData.filter((r) => r.status === "valid" && r.type === "INSERT").length;
  const updateCount = previewData.filter((r) => r.status === "valid" && r.type === "UPDATE").length;

  // Filtered & Paginated Export Data (supports both Saved Records & Active Staff pre-filled)
  const exportSourceList = exportPreviewMode === "active" ? activeStaffPayrollList : existingPayrollList;

  const filteredExportList = useMemo(() => {
    if (!exportSearch) return exportSourceList;
    const s = exportSearch.toLowerCase();
    return exportSourceList.filter((r) => {
      const code = String(r.EmployeeCode || r.CardNo || "").toLowerCase();
      const name = String(r.EmployeeName || r.Name || "").toLowerCase();
      const dept = String(r.DepartmentName || r.Department || "").toLowerCase();
      return code.includes(s) || name.includes(s) || dept.includes(s);
    });
  }, [exportSourceList, exportSearch]);

  const totalExportPages = Math.ceil(filteredExportList.length / exportPageSize) || 1;
  const currentExportPage = Math.min(exportPage, totalExportPages);
  const paginatedExportRows = filteredExportList.slice((currentExportPage - 1) * exportPageSize, currentExportPage * exportPageSize);

  // Filter Guidelines search
  const filteredGuidelines = useMemo(() => {
    if (!guidelineSearch.trim()) return FORMAT_GUIDELINES;
    const q = guidelineSearch.toLowerCase();
    return FORMAT_GUIDELINES.map((cat) => ({
      ...cat,
      fields: cat.fields.filter(
        (f) =>
          f.field.toLowerCase().includes(q) ||
          f.format.toLowerCase().includes(q) ||
          f.rule.toLowerCase().includes(q) ||
          f.allowed.toLowerCase().includes(q)
      ),
    })).filter((cat) => cat.fields.length > 0);
  }, [guidelineSearch]);

  // Sticky table helper styles
  const stickyTh = (left, zIndex, extra = {}) => ({
    position: "sticky",
    left,
    zIndex,
    background: "#f8fafc",
    borderBottom: "2px solid #cbd5e1",
    padding: "10px 12px",
    fontWeight: 700,
    fontSize: 12,
    color: "#334155",
    whiteSpace: "nowrap",
    ...extra,
  });

  const stickyTd = (left, zIndex, bg, extra = {}) => ({
    position: "sticky",
    left,
    zIndex,
    background: bg || "#fff",
    borderBottom: "1px solid #e2e8f0",
    padding: "8px 12px",
    fontSize: 12,
    whiteSpace: "nowrap",
    ...extra,
  });

  const standardTh = (extra = {}) => ({
    background: "#f8fafc",
    borderBottom: "2px solid #cbd5e1",
    padding: "10px 12px",
    fontWeight: 700,
    fontSize: 12,
    color: "#334155",
    whiteSpace: "nowrap",
    ...extra,
  });

  const standardTd = (extra = {}) => ({
    borderBottom: "1px solid #e2e8f0",
    padding: "8px 12px",
    fontSize: 12,
    whiteSpace: "nowrap",
    ...extra,
  });

  // Reusable Pagination Bar
  const renderPaginationBar = (currentPage, totalPages, pageSize, setPageSize, setPage, totalItems, label = "records") => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    for (let p = start; p <= end; p++) pages.push(p);

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 20px",
          background: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#64748b" }}>
          <span>Show</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 75, fontWeight: 600 }}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span>
            entries | Showing {totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1} to{" "}
            {Math.min(currentPage * pageSize, totalItems)} of {totalItems} {label}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={currentPage === 1}
            onClick={() => setPage(1)}
            style={{ borderRadius: 6, padding: "4px 8px" }}
            title="First Page"
          >
            «
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={currentPage === 1}
            onClick={() => setPage(Math.max(1, currentPage - 1))}
            style={{ borderRadius: 6, padding: "4px 10px", fontWeight: 600 }}
          >
            ‹ Prev
          </button>

          {start > 1 && <span style={{ padding: "0 4px", color: "#94a3b8" }}>...</span>}

          {pages.map((p) => (
            <button
              key={p}
              type="button"
              className={`btn btn-sm ${p === currentPage ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setPage(p)}
              style={{ minWidth: 32, borderRadius: 6, fontWeight: p === currentPage ? 700 : 500 }}
            >
              {p}
            </button>
          ))}

          {end < totalPages && <span style={{ padding: "0 4px", color: "#94a3b8" }}>...</span>}

          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={currentPage >= totalPages}
            onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
            style={{ borderRadius: 6, padding: "4px 10px", fontWeight: 600 }}
          >
            Next ›
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={currentPage >= totalPages}
            onClick={() => setPage(totalPages)}
            style={{ borderRadius: 6, padding: "4px 8px" }}
            title="Last Page"
          >
            »
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: "20px 24px", minHeight: "100vh", background: "#f4f6fb", fontFamily: "'Poppins', sans-serif" }}>
      {/* ── TOP HEADER / SESSION BAR ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
          background: "#fff",
          padding: "16px 24px",
          borderRadius: 14,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => navigate(-1)}
            style={{ borderRadius: 8, padding: "6px 14px", fontWeight: 600 }}
          >
            ← Back
          </button>
          <div>
            <h3 style={{ margin: 0, fontWeight: 700, color: "#1e293b", fontSize: 20 }}>
              📊 Monthly Payroll — Import / Export
            </h3>
            <span style={{ fontSize: 13, color: "#64748b" }}>
              Firm: <strong>{selectedFirm?.Name || "Selected Firm"}</strong> | Period: <strong>{selectedMonth} {selectedYear}</strong>
            </span>
          </div>
        </div>

        {/* Month / Year Selectors & Guidelines Button */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <select
            className="form-select form-select-sm"
            style={{ width: 130, fontWeight: 600 }}
            value={selectedMonth}
            onChange={(e) => handleMonthChange(e.target.value)}
          >
            {MONTH_NAMES.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>

          <input
            type="number"
            className="form-control form-control-sm text-center"
            style={{ width: 85, fontWeight: 600 }}
            value={selectedYear}
            onChange={(e) => handleYearChange(e.target.value)}
          />

          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            style={{ borderRadius: 8, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}
            onClick={() => setShowColumnModal(true)}
            title="Choose which columns to export and display"
          >
            <span>⚙️</span> Select Columns ({selectedColumnKeys.length}/{activePayrollFields.length})
          </button>

          <button
            className="btn btn-outline-info btn-sm"
            style={{ borderRadius: 8, fontWeight: 600 }}
            onClick={() => setShowGuidelinesModal(true)}
          >
            ℹ️ Field Guidelines
          </button>
        </div>
      </div>

      {/* ── DUAL-CARD GRID (EXPORT & IMPORT) ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
          gap: 20,
          marginBottom: 24,
        }}
      >
        {/* ── 1. EXPORT CARD ── */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            overflow: "hidden",
            border: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #0f766e, #0d9488)",
              padding: "16px 22px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #0f766e",
            }}
          >
            <div>
              <h4 style={{ margin: 0, fontWeight: 700, fontSize: 17, color: "#ffffff", display: "flex", alignItems: "center", gap: 8 }}>
                <span>📤</span> Export Payroll Data
              </h4>
              <div style={{ margin: "4px 0 0", color: "#ccfbf1", fontSize: 12.5, fontWeight: 500 }}>
                {selectedMonth} {selectedYear} • {selectedFirm?.Name || "Selected Firm"}
              </div>
            </div>
            {existingPayrollList.length > 0 && (
              <span
                style={{
                  background: "rgba(255,255,255,0.22)",
                  color: "#ffffff",
                  border: "1px solid rgba(255,255,255,0.4)",
                  borderRadius: 20,
                  padding: "4px 12px",
                  fontSize: 11.5,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {existingPayrollList.length} Processed
              </span>
            )}
          </div>

          <div style={{ padding: 20, flex: 1, display: "flex", flexDirection: "column" }}>
            {/* Stats Summary */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "#166534", fontWeight: 700, textTransform: "uppercase" }}>
                  Saved in DB
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#059669" }}>
                  {loadingData ? "..." : existingPayrollList.length}
                </div>
              </div>
              <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "#1e40af", fontWeight: 700, textTransform: "uppercase" }}>
                  Active Staff
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#2563eb" }}>
                  {loadingData ? "..." : activeStaffPayrollList.length}
                </div>
              </div>
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "#475569", fontWeight: 700, textTransform: "uppercase" }}>
                  Month Days
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#1e293b" }}>
                  {getCalculatedMonthDays(selectedMonth, selectedYear, payrollParams)}d
                </div>
              </div>
            </div>

            {/* ── 3 SEPARATE DISTINCT EXPORT ACTION CARDS ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: "auto" }}>
              <style>{`
                .export-action-card {
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  padding: 12px 14px;
                  border-radius: 10px;
                  cursor: pointer;
                  transition: all 0.2s ease-in-out;
                  border-width: 1.5px;
                  border-style: solid;
                  text-decoration: none;
                }
                .export-action-card:hover {
                  transform: translateY(-2px);
                  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .export-action-card.disabled {
                  opacity: 0.6;
                  cursor: not-allowed;
                  pointer-events: none;
                }
              `}</style>

              {/* OPTION 1: Export Saved Monthly Payroll */}
              <div
                role="button"
                tabIndex={0}
                className={`export-action-card ${exportingSaved || loadingData ? "disabled" : ""}`}
                style={{
                  background: "#f0fdf4",
                  borderColor: "#86efac",
                  borderLeft: "5px solid #059669",
                }}
                onClick={handleExportSavedPayroll}
                title="Option 1: Export saved database payroll transactions for this month"
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: "#dcfce7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    📥
                  </div>
                  <div style={{ textAlign: "left", minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#065f46", lineHeight: 1.3 }}>
                      {exportingSaved ? "⏳ Generating Excel..." : "1. Export Saved Month Payroll (.xlsx)"}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 500, color: "#047857", marginTop: 2 }}>
                      Existing saved records in database for {selectedMonth} {selectedYear}
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 10 }}>
                  <span
                    style={{
                      background: "#dcfce7",
                      color: "#065f46",
                      border: "1px solid #86efac",
                      borderRadius: 12,
                      padding: "3px 9px",
                      fontSize: 11,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {existingPayrollList.length} Records
                  </span>
                  <span
                    style={{
                      background: "#059669",
                      color: "#ffffff",
                      borderRadius: 6,
                      padding: "5px 12px",
                      fontSize: 11.5,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      boxShadow: "0 2px 4px rgba(5,150,105,0.25)",
                    }}
                  >
                    Export ⬇
                  </span>
                </div>
              </div>

              {/* OPTION 2: Export Active Employees Pre-filled with Form Data */}
              <div
                role="button"
                tabIndex={0}
                className={`export-action-card ${exportingActive || loadingData ? "disabled" : ""}`}
                style={{
                  background: "#eff6ff",
                  borderColor: "#bfdbfe",
                  borderLeft: "5px solid #2563eb",
                }}
                onClick={handleExportActiveStaffPayroll}
                title="Option 2: Export all active employees pre-filled with salary, days, allowances & formulas exactly as in Payroll form"
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: "#dbeafe",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    👥
                  </div>
                  <div style={{ textAlign: "left", minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1e3a8a", lineHeight: 1.3 }}>
                      {exportingActive ? "⏳ Generating Excel..." : "2. Export Active Staff (Pre-filled Payroll Form) (.xlsx)"}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 500, color: "#1d4ed8", marginTop: 2 }}>
                      All active employees with salary, days & calculated formulas (Ready to edit)
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 10 }}>
                  <span
                    style={{
                      background: "#dbeafe",
                      color: "#1e40af",
                      border: "1px solid #bfdbfe",
                      borderRadius: 12,
                      padding: "3px 9px",
                      fontSize: 11,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {activeStaffPayrollList.length} Active Staff
                  </span>
                  <span
                    style={{
                      background: "#2563eb",
                      color: "#ffffff",
                      borderRadius: 6,
                      padding: "5px 12px",
                      fontSize: 11.5,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      boxShadow: "0 2px 4px rgba(37,99,235,0.25)",
                    }}
                  >
                    Export ⬇
                  </span>
                </div>
              </div>

              {/* OPTION 3: Download Blank Payroll Template */}
              <div
                role="button"
                tabIndex={0}
                className={`export-action-card ${downloadingTemplate || loadingData ? "disabled" : ""}`}
                style={{
                  background: "#f8fafc",
                  borderColor: "#cbd5e1",
                  borderLeft: "5px solid #475569",
                }}
                onClick={handleDownloadBlankTemplate}
                title="Option 3: Download empty template with header definitions and sample guide row"
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: "#e2e8f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    📋
                  </div>
                  <div style={{ textAlign: "left", minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>
                      {downloadingTemplate ? "⏳ Preparing..." : "3. Download Blank Payroll Template (.xlsx)"}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 500, color: "#475569", marginTop: 2 }}>
                      Clean template structure with column guide & sample guidance row
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 10 }}>
                  <span
                    style={{
                      background: "#f1f5f9",
                      color: "#334155",
                      border: "1px solid #cbd5e1",
                      borderRadius: 12,
                      padding: "3px 9px",
                      fontSize: 11,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Blank Format
                  </span>
                  <span
                    style={{
                      background: "#475569",
                      color: "#ffffff",
                      borderRadius: 6,
                      padding: "5px 12px",
                      fontSize: 11.5,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      boxShadow: "0 2px 4px rgba(71,85,105,0.25)",
                    }}
                  >
                    Download ⬇
                  </span>
                </div>
              </div>

              {/* Column Selector & Preview Toggle Buttons */}
              <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                <button
                  type="button"
                  style={{
                    flex: 1,
                    fontWeight: 600,
                    borderRadius: 8,
                    padding: "8px 10px",
                    background: "#f0fdfa",
                    color: "#0f766e",
                    border: "1px solid #99f6e4",
                    fontSize: 12,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    transition: "all 0.2s",
                  }}
                  onClick={() => setShowColumnModal(true)}
                  title="Configure export columns"
                >
                  ⚙️ Select Columns ({selectedColumnKeys.length}/{activePayrollFields.length})
                </button>

                {(existingPayrollList.length > 0 || activeStaffPayrollList.length > 0) && (
                  <button
                    type="button"
                    style={{
                      flex: 1,
                      fontWeight: 600,
                      borderRadius: 8,
                      padding: "8px 10px",
                      background: showExportPreview ? "#1e293b" : "#f1f5f9",
                      color: showExportPreview ? "#ffffff" : "#334155",
                      border: "1px solid #cbd5e1",
                      fontSize: 12,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      transition: "all 0.2s",
                    }}
                    onClick={() => setShowExportPreview(!showExportPreview)}
                  >
                    {showExportPreview ? "▲ Hide Grid" : "👁️ Preview Grid"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── 2. IMPORT CARD ── */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            overflow: "hidden",
            border: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #1e40af, #2563eb)",
              padding: "16px 22px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #1e40af",
            }}
          >
            <div>
              <h4 style={{ margin: 0, fontWeight: 700, fontSize: 17, color: "#ffffff", display: "flex", alignItems: "center", gap: 8 }}>
                <span>📥</span> Import Monthly Payroll
              </h4>
              <div style={{ margin: "4px 0 0", color: "#dbeafe", fontSize: 12.5, fontWeight: 500 }}>
                Upload .xlsx to batch-insert or update monthly payroll
              </div>
            </div>
          </div>

          <div style={{ padding: 22, flex: 1, display: "flex", flexDirection: "column" }}>
            {/* Drag & Drop File Zone */}
            <div
              style={{
                border: fileVerification?.hasMismatch
                  ? "2px dashed #f43f5e"
                  : fileVerification?.fileName
                  ? "2px dashed #10b981"
                  : "2px dashed #93c5fd",
                borderRadius: 12,
                padding: "20px 16px",
                textAlign: "center",
                background: fileVerification?.hasMismatch
                  ? "#fff1f2"
                  : fileVerification?.fileName
                  ? "#f0fdf4"
                  : "#f8faff",
                cursor: "pointer",
                marginBottom: 14,
                transition: "all 0.2s ease",
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <div style={{ fontSize: 32, marginBottom: 6 }}>
                {fileVerification?.hasMismatch ? "⚠️" : fileVerification?.fileName ? "📄" : "📂"}
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: fileVerification?.hasMismatch ? "#be123c" : "#1e3a8a",
                }}
              >
                {importFile ? importFile.name : "Click to select or drag & drop Payroll Excel (.xlsx)"}
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                Supports exported payroll sheets & blank templates
              </div>
            </div>

            {/* ── CARD-LEVEL TRIPLE VERIFICATION ALERT / BADGE ── */}
            {fileVerification && fileVerification.fileName && (
              <div
                style={{
                  background: fileVerification.hasMismatch ? "#fff1f2" : "#f0fdf4",
                  border: `1.5px solid ${fileVerification.hasMismatch ? "#fda4af" : "#86efac"}`,
                  borderRadius: 10,
                  padding: "12px 14px",
                  marginBottom: 14,
                  boxShadow: fileVerification.hasMismatch ? "0 2px 8px rgba(225,29,72,0.12)" : "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 13, color: fileVerification.hasMismatch ? "#9f1239" : "#166534" }}>
                    <span>{fileVerification.hasMismatch ? "🚨 MISMATCH DETECTED" : "🛡️ TRIPLE VERIFIED & SAFE"}</span>
                  </div>
                  <span
                    style={{
                      background: fileVerification.hasMismatch ? "#e11d48" : "#16a34a",
                      color: "#fff",
                      fontSize: 10.5,
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: 12,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {fileVerification.hasMismatch
                      ? fileVerification.firmMismatch
                        ? "Firm Mismatch"
                        : "Month / Year Mismatch"
                      : "Verified & Safe"}
                  </span>
                </div>

                <div style={{ fontSize: 11.5, color: fileVerification.hasMismatch ? "#be123c" : "#15803d", lineHeight: 1.6 }}>
                  <div>🏢 <strong>Firm Check:</strong> File: <span style={{ fontWeight: 600 }}>{fileVerification.detectedFirm || selectedFirm?.Name || "Not Specified"}</span> | Active Session: <span style={{ fontWeight: 700, color: "#1e293b" }}>{selectedFirm?.Name || "Selected Firm"}</span></div>
                  <div>📁 <strong>Month Check:</strong> File: <span style={{ fontWeight: 600 }}>{fileVerification.fileMonth || fileVerification.detectedMonth || "Not Specified"}</span> | Active Session: <span style={{ fontWeight: 700, color: "#1e293b" }}>{selectedMonth}</span></div>
                  <div>📅 <strong>Year Check:</strong> File: <span style={{ fontWeight: 600 }}>{fileVerification.fileYear || fileVerification.detectedYear || "Not Specified"}</span> | Active Session: <span style={{ fontWeight: 700, color: "#1e293b" }}>{selectedYear}</span></div>
                </div>

                {fileVerification.hasMismatch && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: "6px 10px",
                      background: "#fee2e2",
                      borderRadius: 6,
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: "#9f1239",
                      borderLeft: "3px solid #e11d48",
                    }}
                  >
                    ⚠️ {fileVerification.mismatchReason}
                  </div>
                )}
              </div>
            )}

            {isParsing && (
              <div style={{ textAlign: "center", color: "#3b82f6", fontWeight: 600, marginBottom: 12 }}>
                ⏳ Parsing & Validating Excel Data...
              </div>
            )}

            {showPreview && (
              <div style={{ display: "flex", gap: 10, marginTop: "auto" }}>
                <button
                  className="btn btn-outline-secondary"
                  style={{ fontWeight: 600, borderRadius: 8, padding: "10px 16px" }}
                  onClick={resetImport}
                  disabled={importing}
                >
                  ✕ Clear
                </button>
                <button
                  className={fileVerification?.hasMismatch ? "btn btn-danger" : "btn btn-primary"}
                  style={{
                    flex: 1,
                    fontWeight: 700,
                    borderRadius: 8,
                    padding: "10px",
                    cursor: (importing || validCount === 0 || fileVerification?.hasMismatch) ? "not-allowed" : "pointer",
                    opacity: fileVerification?.hasMismatch ? 0.6 : 1,
                    background: fileVerification?.hasMismatch ? "#ef4444" : undefined,
                    borderColor: fileVerification?.hasMismatch ? "#dc2626" : undefined,
                  }}
                  onClick={handleImportSubmit}
                  disabled={importing || validCount === 0 || Boolean(fileVerification?.hasMismatch)}
                  title={
                    fileVerification?.hasMismatch
                      ? `Import Blocked: ${fileVerification.mismatchReason}`
                      : `Import ${validCount} valid records into ${selectedFirm?.Name} (${selectedMonth} ${selectedYear})`
                  }
                >
                  {importing
                    ? "Importing to Database..."
                    : fileVerification?.hasMismatch
                    ? `🚫 Import Blocked (${fileVerification.firmMismatch ? "Firm Mismatch" : "Period Mismatch"})`
                    : `🚀 Import ${validCount} Valid Records`}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── EXISTING PAYROLL EXPORT PREVIEW TABLE (PAGINATED) ── */}
      {showExportPreview && (existingPayrollList.length > 0 || activeStaffPayrollList.length > 0) && (
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            padding: 24,
            marginBottom: 28,
            border: "1px solid #e2e8f0",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
                <h5 style={{ margin: 0, fontWeight: 700, color: exportPreviewMode === "active" ? "#1d4ed8" : "#0f766e" }}>
                  {exportPreviewMode === "active"
                    ? `👥 Active Staff Form Preview (${selectedMonth} ${selectedYear})`
                    : `📤 Saved Payroll Data Preview (${selectedMonth} ${selectedYear})`}
                </h5>

                {/* Segmented Mode Selector for Preview */}
                <div style={{ display: "inline-flex", borderRadius: 8, overflow: "hidden", border: "1px solid #cbd5e1" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setExportPreviewMode("saved");
                      setExportPage(1);
                    }}
                    style={{
                      padding: "5px 12px",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      border: "none",
                      background: exportPreviewMode === "saved" ? "#059669" : "#ffffff",
                      color: exportPreviewMode === "saved" ? "#ffffff" : "#065f46",
                      transition: "all 0.15s",
                    }}
                  >
                    📥 1. Saved Records ({existingPayrollList.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setExportPreviewMode("active");
                      setExportPage(1);
                    }}
                    style={{
                      padding: "5px 12px",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      border: "none",
                      borderLeft: "1px solid #cbd5e1",
                      background: exportPreviewMode === "active" ? "#2563eb" : "#ffffff",
                      color: exportPreviewMode === "active" ? "#ffffff" : "#1e40af",
                      transition: "all 0.15s",
                    }}
                  >
                    👥 2. Active Staff Pre-filled ({activeStaffPayrollList.length})
                  </button>
                </div>
              </div>
              <span style={{ fontSize: 12, color: "#64748b" }}>
                Showing {filteredExportList.length} records • {exportPreviewMode === "active" ? "Pre-calculated using Payroll form formulas (Ready to export/edit)" : "Saved database transaction records"}
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {/* Quick Export Button for current preview mode */}
              {exportPreviewMode === "active" ? (
                <button
                  type="button"
                  style={{
                    fontWeight: 600,
                    borderRadius: 8,
                    fontSize: 12,
                    padding: "6px 14px",
                    background: "#2563eb",
                    color: "#ffffff",
                    border: "none",
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(37,99,235,0.2)",
                  }}
                  onClick={handleExportActiveStaffPayroll}
                  disabled={exportingActive || loadingData}
                >
                  {exportingActive ? "⏳ Generating..." : "📥 Download Active Staff (.xlsx)"}
                </button>
              ) : (
                <button
                  type="button"
                  style={{
                    fontWeight: 600,
                    borderRadius: 8,
                    fontSize: 12,
                    padding: "6px 14px",
                    background: "#059669",
                    color: "#ffffff",
                    border: "none",
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(5,150,105,0.2)",
                  }}
                  onClick={handleExportSavedPayroll}
                  disabled={exportingSaved || loadingData || existingPayrollList.length === 0}
                >
                  {exportingSaved ? "⏳ Generating..." : "📥 Download Saved Payroll (.xlsx)"}
                </button>
              )}

              {/* Quick scroll left/right buttons */}
              <div className="btn-group btn-group-sm">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => exportTableRef.current?.scrollBy({ left: -300, behavior: "smooth" })}
                  title="Scroll Left in table"
                  style={{ borderRadius: "6px 0 0 6px", fontWeight: 600, fontSize: 12 }}
                >
                  ◀ Left
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => exportTableRef.current?.scrollBy({ left: 300, behavior: "smooth" })}
                  title="Scroll Right in table"
                  style={{ borderRadius: "0 6px 6px 0", fontWeight: 600, fontSize: 12 }}
                >
                  Right ▶
                </button>
              </div>

              <div style={{ minWidth: 220 }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="🔍 Search Code, Name, Dept..."
                  value={exportSearch}
                  onChange={(e) => {
                    setExportSearch(e.target.value);
                    setExportPage(1);
                  }}
                />
              </div>
            </div>
          </div>

          <div
            ref={exportTableRef}
            className="table-responsive"
            style={{
              maxHeight: 440,
              overflowX: "auto",
              overflowY: "auto",
              display: "block",
              width: "100%",
              position: "relative",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
            }}
          >
            <table
              className="table table-bordered table-hover table-sm custom-table"
              style={{
                fontSize: 12,
                minWidth: "100%",
                width: "max-content",
                borderCollapse: "separate",
                borderSpacing: 0,
                marginBottom: 0,
              }}
            >
              <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 5 }}>
                <tr>
                  <th style={stickyTh(0, 6, { width: 50, minWidth: 50, maxWidth: 50, textAlign: "center" })}>#</th>
                  <th style={stickyTh(50, 6, { width: 110, minWidth: 110, maxWidth: 110 })}>Emp. Code</th>
                  <th style={stickyTh(160, 6, { width: 170, minWidth: 170, maxWidth: 170, boxShadow: "3px 0 6px -2px rgba(0,0,0,0.12)" })}>Employee Name</th>
                  {(activePayrollFields && activePayrollFields.length > 0 ? activePayrollFields : PAYROLL_FIELDS)
                    .filter((col) => selectedColumnKeys.length === 0 || selectedColumnKeys.includes(col.key))
                    .filter((col) => !["EmployeeCode", "CardNo", "EmployeeName", "Name"].includes(col.key))
                    .map((col) => (
                      <th
                        key={col.key}
                        style={standardTh({
                          minWidth: col.width ? col.width * 8 : 110,
                          textAlign: col.type === "number" ? "right" : "left",
                        })}
                        title={col.key}
                      >
                        {col.label}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {paginatedExportRows.map((r, i) => (
                  <tr key={i}>
                    <td style={stickyTd(0, 4, "#fff", { width: 50, minWidth: 50, maxWidth: 50, textAlign: "center", fontWeight: 600 })}>
                      {(currentExportPage - 1) * exportPageSize + i + 1}
                    </td>
                    <td style={stickyTd(50, 4, "#fff", { width: 110, minWidth: 110, maxWidth: 110, fontWeight: 600, color: "#1d4ed8" })}>
                      {r.EmployeeCode || r.CardNo || "-"}
                    </td>
                    <td
                      style={stickyTd(160, 4, "#fff", {
                        width: 170,
                        minWidth: 170,
                        maxWidth: 170,
                        fontWeight: 600,
                        boxShadow: "3px 0 6px -2px rgba(0,0,0,0.12)",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      })}
                      title={r.EmployeeName || r.Name || ""}
                    >
                      {r.EmployeeName || r.Name || "-"}
                    </td>
                    {(activePayrollFields && activePayrollFields.length > 0 ? activePayrollFields : PAYROLL_FIELDS)
                      .filter((col) => selectedColumnKeys.length === 0 || selectedColumnKeys.includes(col.key))
                      .filter((col) => !["EmployeeCode", "CardNo", "EmployeeName", "Name"].includes(col.key))
                      .map((col) => {
                        const val = getFieldValueFromRow(r, col);
                        const isNum = col.type === "number";
                        return (
                          <td
                            key={col.key}
                            style={standardTd({
                              minWidth: col.width ? col.width * 8 : 110,
                              textAlign: isNum ? "right" : "left",
                              fontWeight: ["NetAmount", "GrossAmount", "PaidDay"].includes(col.key) ? 600 : 400,
                              color: col.key === "NetAmount" ? "#059669" : col.key === "TotalDeduction" ? "#dc2626" : undefined,
                            })}
                          >
                            {isNum ? parseNum(val).toFixed(2) : (val || "-")}
                          </td>
                        );
                      })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {renderPaginationBar(
            currentExportPage,
            totalExportPages,
            exportPageSize,
            setExportPageSize,
            setExportPage,
            filteredExportList.length,
            "employees"
          )}
        </div>
      )}

      {/* ── IMPORT PREVIEW SECTION (DYNAMIC COLUMNS & PAGINATED) ── */}
      {showPreview && previewData.length > 0 && (
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            padding: 24,
            marginBottom: 28,
            border: "1px solid #e2e8f0",
          }}
        >
          {/* ─── Double Verification Banner (File Name + Tab Name) ─── */}
          {fileVerification && fileVerification.fileName && (
            <div
              style={{
                background: fileVerification.hasMismatch ? "#fff1f2" : "#f0fdf4",
                border: `1.5px solid ${fileVerification.hasMismatch ? "#fda4af" : "#86efac"}`,
                borderRadius: 12,
                padding: "12px 18px",
                marginBottom: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: fileVerification.hasMismatch ? "#fee2e2" : "#dcfce7",
                    color: fileVerification.hasMismatch ? "#e11d48" : "#16a34a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {fileVerification.hasMismatch ? "⚠️" : "🛡️"}
                </div>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: fileVerification.hasMismatch ? "#9f1239" : "#166534" }}>
                    {fileVerification.hasMismatch ? "Triple Verification Alert — Firm / Period Mismatch!" : "Triple Verification Passed (Firm, Month & Year Verified)"}
                  </div>
                  <div style={{ fontSize: 12, color: fileVerification.hasMismatch ? "#be123c" : "#15803d", marginTop: 3, lineHeight: 1.5 }}>
                    🏢 <strong>Firm:</strong> File: {fileVerification.detectedFirm || "Not Specified"} | Session: {selectedFirm?.Name || "Selected Firm"} &nbsp;|&nbsp;
                    📁 <strong>File:</strong> {fileVerification.fileName} &nbsp;|&nbsp;
                    📑 <strong>Tab:</strong> {fileVerification.sheetName} &nbsp;|&nbsp;
                    🎯 <strong>Target:</strong> {selectedMonth} {selectedYear}
                  </div>
                  {fileVerification.hasMismatch && (
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#e11d48", marginTop: 4 }}>
                      🚨 {fileVerification.mismatchReason}
                    </div>
                  )}
                </div>
              </div>
              <span
                style={{
                  background: fileVerification.hasMismatch ? "#e11d48" : "#16a34a",
                  color: "#ffffff",
                  padding: "5px 14px",
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                }}
              >
                {fileVerification.hasMismatch ? "MISMATCH DETECTED" : "VERIFIED & SAFE"}
              </span>
            </div>
          )}

          {/* Summary Badges, Quick Scroll & Search */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 18 }}>
            <span style={{ fontWeight: 700, fontSize: 16, color: "#1e293b", marginRight: 6 }}>
              📋 Preview & Validation:
            </span>
            <button
              className={`btn btn-sm ${previewFilter === "all" ? "btn-dark" : "btn-outline-dark"}`}
              style={{ borderRadius: 20 }}
              onClick={() => { setPreviewFilter("all"); setImportPage(1); }}
            >
              All ({previewData.length})
            </button>
            <button
              className={`btn btn-sm ${previewFilter === "insert" ? "btn-success" : "btn-outline-success"}`}
              style={{ borderRadius: 20 }}
              onClick={() => { setPreviewFilter("insert"); setImportPage(1); }}
            >
              ➕ New Insert ({insertCount})
            </button>
            <button
              className={`btn btn-sm ${previewFilter === "update" ? "btn-primary" : "btn-outline-primary"}`}
              style={{ borderRadius: 20 }}
              onClick={() => { setPreviewFilter("update"); setImportPage(1); }}
            >
              🔄 Update Existing ({updateCount})
            </button>
            {errorCount > 0 && (
              <button
                className={`btn btn-sm ${previewFilter === "error" ? "btn-danger" : "btn-outline-danger"}`}
                style={{ borderRadius: 20 }}
                onClick={() => { setPreviewFilter("error"); setImportPage(1); }}
              >
                ⚠️ Errors ({errorCount})
              </button>
            )}

            {/* Quick scroll left/right buttons */}
            <div className="btn-group btn-group-sm" style={{ marginLeft: 6 }}>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => importTableRef.current?.scrollBy({ left: -350, behavior: "smooth" })}
                title="Scroll Left in table"
                style={{ borderRadius: "6px 0 0 6px", fontWeight: 600, fontSize: 12 }}
              >
                ◀ Left
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => importTableRef.current?.scrollBy({ left: 350, behavior: "smooth" })}
                title="Scroll Right in table"
                style={{ borderRadius: "0 6px 6px 0", fontWeight: 600, fontSize: 12 }}
              >
                Right ▶
              </button>
            </div>

            <div style={{ marginLeft: "auto", minWidth: 240 }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="🔍 Search Code, Name, Dept..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setImportPage(1); }}
              />
            </div>
          </div>

          {/* Progress bar during import */}
          {importing && (
            <div className="progress mb-3" style={{ height: 10, borderRadius: 5 }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                style={{ width: `${importProgress}%` }}
              />
            </div>
          )}

          {/* Validation Errors Notice */}
          {errorCount > 0 && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 10,
                padding: "10px 16px",
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                fontSize: 13,
                color: "#991b1b",
              }}
            >
              <div>
                <strong>⚠️ {errorCount} Row(s) with Validation Errors:</strong> Basic Salary must be &gt; 0, Working Days / Paid Days cannot exceed Month Days, and duplicates are flagged. These rows cannot be imported until corrected.
              </div>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                style={{ borderRadius: 6, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" }}
                onClick={() => { setPreviewFilter("error"); setImportPage(1); }}
              >
                View Errors ({errorCount})
              </button>
            </div>
          )}

          {/* Dynamic Table with Sticky Left Columns */}
          <div
            ref={importTableRef}
            className="table-responsive"
            style={{
              maxHeight: 520,
              overflowX: "auto",
              overflowY: "auto",
              display: "block",
              width: "100%",
              position: "relative",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
            }}
          >
            <table
              className="table table-bordered table-hover table-sm custom-table"
              style={{
                fontSize: 12,
                minWidth: "100%",
                width: "max-content",
                borderCollapse: "separate",
                borderSpacing: 0,
                marginBottom: 0,
              }}
            >
              <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 6 }}>
                <tr>
                  <th style={stickyTh(0, 7, { width: 50, minWidth: 50, maxWidth: 50, textAlign: "center" })}>#</th>
                  <th style={stickyTh(50, 7, { width: 85, minWidth: 85, maxWidth: 85, textAlign: "center" })}>Status</th>
                  <th style={stickyTh(135, 7, { width: 85, minWidth: 85, maxWidth: 85, textAlign: "center" })}>Type</th>
                  <th style={stickyTh(220, 7, { width: 175, minWidth: 175, maxWidth: 175 })}>Validation / Errors</th>
                  <th style={stickyTh(395, 7, { width: 110, minWidth: 110, maxWidth: 110 })}>Emp. Code</th>
                  <th style={stickyTh(505, 7, { width: 160, minWidth: 160, maxWidth: 160, boxShadow: "3px 0 6px -2px rgba(0,0,0,0.12)" })}>
                    Employee Name
                  </th>

                  {/* Dynamic Columns from Imported Excel */}
                  {importedColumns
                    .filter((c) => !["EmployeeCode", "CardNo", "EmployeeName", "Name"].includes(c.key))
                    .map((col) => (
                      <th
                        key={col.key}
                        style={standardTh({
                          textAlign: col.type === "number" ? "right" : "left",
                          minWidth: 120,
                        })}
                        title={col.key}
                      >
                        {col.label}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {paginatedImportRows.length > 0 ? (
                  paginatedImportRows.map((r, i) => {
                    const rowBg = r.status === "error" ? "#fff1f2" : i % 2 === 0 ? "#fff" : "#fafafa";
                    const globalIdx = (currentImportPage - 1) * importPageSize + i + 1;

                    return (
                      <tr key={i} style={{ background: rowBg }}>
                        {/* Sticky Identification Columns */}
                        <td style={stickyTd(0, 5, rowBg, { width: 50, minWidth: 50, maxWidth: 50, textAlign: "center", fontWeight: 600 })} title={`Excel Row: ${r._rowIndex}`}>
                          {r._sNo || globalIdx}
                        </td>
                        <td style={stickyTd(50, 5, rowBg, { width: 85, minWidth: 85, maxWidth: 85, textAlign: "center" })}>
                          {r.status === "error" ? (
                            <span className="badge badge-danger">INVALID</span>
                          ) : (
                            <span className="badge badge-success">VALID</span>
                          )}
                        </td>
                        <td style={stickyTd(135, 5, rowBg, { width: 85, minWidth: 85, maxWidth: 85, textAlign: "center" })}>
                          {r.type === "INSERT" ? (
                            <span className="badge badge-success">NEW</span>
                          ) : r.type === "UPDATE" ? (
                            <span className="badge badge-primary">UPDATE</span>
                          ) : (
                            <span className="badge badge-secondary">INVALID</span>
                          )}
                        </td>
                        <td
                          style={stickyTd(220, 5, rowBg, {
                            width: 175,
                            minWidth: 175,
                            maxWidth: 175,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: r.errors?.length > 0 ? "#dc2626" : "#059669",
                            fontWeight: r.errors?.length > 0 ? 600 : 500,
                          })}
                          title={r.errors?.length > 0 ? r.errors.join(", ") : "Ready to Import"}
                        >
                          {r.errors?.length > 0 ? r.errors.join(", ") : "Ready to Import"}
                        </td>
                        <td style={stickyTd(395, 5, rowBg, { width: 110, minWidth: 110, maxWidth: 110, fontWeight: 600, color: "#1d4ed8" })}>
                          {r.EmployeeCode || r.CardNo || "-"}
                        </td>
                        <td
                          style={stickyTd(505, 5, rowBg, {
                            width: 160,
                            minWidth: 160,
                            maxWidth: 160,
                            fontWeight: 600,
                            boxShadow: "3px 0 6px -2px rgba(0,0,0,0.12)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          })}
                          title={r.EmployeeName || ""}
                        >
                          {r.EmployeeName || "-"}
                        </td>

                        {/* Dynamic Column Data */}
                        {importedColumns
                          .filter((c) => !["EmployeeCode", "CardNo", "EmployeeName", "Name"].includes(c.key))
                          .map((col) => {
                            const val = r[col.key];
                            const isNum = col.type === "number" || typeof val === "number";

                            return (
                              <td
                                key={col.key}
                                style={standardTd({
                                  minWidth: 120,
                                  textAlign: isNum ? "right" : "left",
                                  fontWeight: ["GrossAmount", "NetAmount", "PaidDay"].includes(col.key) ? 600 : 400,
                                  color: col.key === "NetAmount" ? "#059669" : undefined,
                                })}
                              >
                                {isNum && val !== undefined && val !== null
                                  ? parseNum(val).toFixed(2)
                                  : val !== undefined && val !== null
                                  ? String(val)
                                  : "-"}
                              </td>
                            );
                          })}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={importedColumns.length + 6} className="text-center py-4 text-muted">
                      No matching records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Import Pagination Footer */}
          {renderPaginationBar(
            currentImportPage,
            totalImportPages,
            importPageSize,
            setImportPageSize,
            setImportPage,
            filteredPreview.length,
            "rows"
          )}
        </div>
      )}

      {/* ── FORMAT GUIDELINES & CODES MODAL ── */}
      {showGuidelinesModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            backdropFilter: "blur(4px)",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowGuidelinesModal(false);
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 20,
              width: "100%",
              maxWidth: 900,
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #1e40af, #3b82f6)",
                padding: "20px 24px",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>
                  📖 Payroll Column Guidelines & Calculation Codes
                </h4>
                <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: 13 }}>
                  Cheat sheet for matching rules, statutory contribution formulas, and column formats
                </p>
              </div>
              <button
                className="btn btn-sm btn-light"
                style={{ borderRadius: 8, fontWeight: 600 }}
                onClick={() => setShowGuidelinesModal(false)}
              >
                ✕ Close
              </button>
            </div>

            {/* Search Bar */}
            <div style={{ padding: "12px 24px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", display: "flex", gap: 10 }}>
              <input
                type="text"
                placeholder="🔍 Search guidelines (e.g. PF, ESI, Pension, BasicSalary, Overtime)..."
                value={guidelineSearch}
                onChange={(e) => setGuidelineSearch(e.target.value)}
                style={{
                  flex: 1,
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  fontSize: 13,
                  outline: "none",
                }}
              />
              {guidelineSearch && (
                <button
                  onClick={() => setGuidelineSearch("")}
                  style={{
                    background: "#e5e7eb",
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 12px",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              )}
            </div>

            {/* Content Body */}
            <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
              {filteredGuidelines.map((cat, idx) => (
                <div key={idx} style={{ marginBottom: 24 }}>
                  <h6 style={{ fontWeight: 700, color: "#1e3a8a", borderBottom: "2px solid #e2e8f0", paddingBottom: 6, marginBottom: 12 }}>
                    {cat.category}
                  </h6>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm" style={{ fontSize: 12.5 }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "25%" }}>Field Header</th>
                          <th style={{ width: "20%" }}>Format & Allowed</th>
                          <th style={{ width: "40%" }}>Rules & Calculations</th>
                          <th style={{ width: "15%" }}>Example</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cat.fields.map((f, fi) => (
                          <tr key={fi}>
                            <td style={{ fontWeight: 600, color: "#1e40af" }}>{f.field}</td>
                            <td>
                              <span className="badge badge-secondary me-1">{f.format}</span>
                              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{f.allowed}</div>
                            </td>
                            <td>{f.rule}</td>
                            <td><code>{f.example}</code></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── COLUMN SELECTION & CUSTOMIZATION MODAL ── */}
      {showColumnModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.65)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 20,
              width: "100%",
              maxWidth: 820,
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #0f766e, #0d9488)",
                padding: "18px 24px",
                color: "#fff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}>
                  <span>⚙️</span> Select Export & Template Columns
                </h4>
                <div style={{ fontSize: 12, color: "#ccfbf1", marginTop: 2 }}>
                  Selected: <strong>{selectedColumnKeys.length}</strong> of <strong>{activePayrollFields.length}</strong> columns active
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-light"
                style={{ borderRadius: 8, fontWeight: 700, padding: "4px 12px" }}
                onClick={() => setShowColumnModal(false)}
              >
                ✕ Close
              </button>
            </div>

            {/* Controls Bar: Presets + Search */}
            <div style={{ padding: "14px 24px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              {/* Presets Chips */}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: "#475569", marginRight: 4 }}>Presets:</span>
                <button
                  type="button"
                  className="btn btn-xs btn-outline-primary"
                  style={{ borderRadius: 20, padding: "3px 10px", fontSize: 11.5, fontWeight: 600 }}
                  onClick={handleSelectAllColumns}
                >
                  ✓ All Columns ({activePayrollFields.length})
                </button>
                <button
                  type="button"
                  className="btn btn-xs btn-outline-secondary"
                  style={{ borderRadius: 20, padding: "3px 10px", fontSize: 11.5, fontWeight: 600 }}
                  onClick={() => handleApplyPreset("standard")}
                >
                  📊 Standard / Summary
                </button>
                <button
                  type="button"
                  className="btn btn-xs btn-outline-info"
                  style={{ borderRadius: 20, padding: "3px 10px", fontSize: 11.5, fontWeight: 600 }}
                  onClick={() => handleApplyPreset("statutory")}
                >
                  🏦 Statutory (PF / ESI)
                </button>
                <button
                  type="button"
                  className="btn btn-xs btn-outline-warning"
                  style={{ borderRadius: 20, padding: "3px 10px", fontSize: 11.5, fontWeight: 600 }}
                  onClick={() => handleApplyPreset("attendance")}
                >
                  📅 Attendance & Days
                </button>
                <button
                  type="button"
                  className="btn btn-xs btn-outline-success"
                  style={{ borderRadius: 20, padding: "3px 10px", fontSize: 11.5, fontWeight: 600 }}
                  onClick={() => handleApplyPreset("salary")}
                >
                  💰 Salary & Earnings
                </button>
                <button
                  type="button"
                  className="btn btn-xs btn-outline-danger"
                  style={{ borderRadius: 20, padding: "3px 10px", fontSize: 11.5, fontWeight: 600 }}
                  onClick={handleDeselectAllColumns}
                >
                  ✕ Mandatory Only
                </button>
              </div>

              {/* Search & Category Filter */}
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ maxWidth: 280, borderRadius: 8 }}
                  placeholder="🔍 Search column name or key..."
                  value={colSearchTerm}
                  onChange={(e) => setColSearchTerm(e.target.value)}
                />
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {["all", "Identification", "Salary", "Attendance", "Earnings", "Statutory", "Deductions", "Flags"].map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setActiveColCategory(cat)}
                      style={{
                        padding: "3px 10px",
                        fontSize: 11,
                        borderRadius: 6,
                        border: "1px solid",
                        borderColor: activeColCategory === cat ? "#0d9488" : "#cbd5e1",
                        background: activeColCategory === cat ? "#0d9488" : "#ffffff",
                        color: activeColCategory === cat ? "#ffffff" : "#334155",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      {cat === "all" ? "All Categories" : cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Columns List / Grid grouped by category */}
            <div style={{ padding: "16px 24px", overflowY: "auto", flex: 1, maxHeight: "55vh" }}>
              {(() => {
                const categories = ["Identification", "Salary", "Attendance", "Earnings", "Statutory", "Deductions", "Summary", "Flags"];
                return categories
                  .filter((cat) => activeColCategory === "all" || activeColCategory === cat)
                  .map((cat) => {
                    const catFields = activePayrollFields.filter((f) => {
                      if (f.category !== cat) return false;
                      if (colSearchTerm.trim()) {
                        const s = colSearchTerm.toLowerCase();
                        return f.label.toLowerCase().includes(s) || f.key.toLowerCase().includes(s) || (f.hint && f.hint.toLowerCase().includes(s));
                      }
                      return true;
                    });

                    if (catFields.length === 0) return null;

                    const allCatFields = activePayrollFields.filter((f) => f.category === cat);
                    const selectedCatCount = allCatFields.filter((f) => selectedColumnKeys.includes(f.key)).length;
                    const allCatSelected = selectedCatCount === allCatFields.length;

                    return (
                      <div key={cat} style={{ marginBottom: 16, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 14px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, borderBottom: "1px solid #e2e8f0", paddingBottom: 6 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>
                            {cat} ({selectedCatCount}/{allCatFields.length})
                          </span>
                          <button
                            type="button"
                            className="btn btn-xs btn-link text-decoration-none"
                            style={{ fontSize: 11.5, fontWeight: 600, padding: 0, color: "#0d9488" }}
                            onClick={() => handleToggleCategory(cat)}
                          >
                            {allCatSelected ? "Deselect Group" : "Select Group"}
                          </button>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8 }}>
                          {catFields.map((field) => {
                            const isSelected = selectedColumnKeys.includes(field.key);
                            const isLocked = field.key === "EmployeeCode";

                            return (
                              <label
                                key={field.key}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  padding: "6px 10px",
                                  borderRadius: 8,
                                  background: isSelected ? "#f0fdf4" : "#ffffff",
                                  border: `1px solid ${isSelected ? "#86efac" : "#e2e8f0"}`,
                                  cursor: isLocked ? "not-allowed" : "pointer",
                                  fontSize: 12,
                                  margin: 0,
                                  userSelect: "none",
                                  transition: "all 0.15s",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled={isLocked}
                                  onChange={() => handleToggleColumn(field.key)}
                                  style={{ cursor: isLocked ? "not-allowed" : "pointer" }}
                                />
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <div style={{ fontWeight: 600, color: isSelected ? "#065f46" : "#334155", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                                    {field.label}
                                    {isLocked && <span style={{ fontSize: 10, color: "#dc2626", marginLeft: 4, fontWeight: 700 }}>(Mandatory)</span>}
                                  </div>
                                  <div style={{ fontSize: 10, color: "#64748b" }}>
                                    [{field.key}] {field.type === "number" ? "• Number" : ""}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
              })()}
            </div>

            {/* Footer Buttons */}
            <div style={{ padding: "14px 24px", background: "#f8fafc", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                Active for: <strong>Saved Export</strong>, <strong>Active Staff Export</strong>, and <strong>Blank Template</strong>
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                style={{ borderRadius: 8, fontWeight: 700, padding: "6px 20px" }}
                onClick={() => setShowColumnModal(false)}
              >
                Apply & Close ({selectedColumnKeys.length} Selected)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── IMPORT RESULT MODAL ── */}
      {showResultModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 20,
              width: "100%",
              maxWidth: 640,
              overflow: "hidden",
              boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
            }}
          >
            <div
              style={{
                background: importResults.failed > 0 ? "linear-gradient(135deg, #d97706, #f59e0b)" : "linear-gradient(135deg, #059669, #10b981)",
                padding: "20px 24px",
                color: "#fff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>
                {importResults.failed > 0 ? "⚠️ Import Finished with Warnings" : "🎉 Import Successful!"}
              </h4>
              <button
                className="btn btn-sm btn-light"
                style={{ borderRadius: 8, fontWeight: 600 }}
                onClick={() => {
                  setShowResultModal(false);
                  resetImport();
                }}
              >
                ✕ Close
              </button>
            </div>

            <div style={{ padding: 24 }}>
              {/* Stats cards */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>TOTAL</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#1e293b" }}>{importResults.total}</div>
                </div>
                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: 12, textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "#166534", fontWeight: 600 }}>INSERTED</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#059669" }}>{importResults.inserted}</div>
                </div>
                <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: 12, textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "#1e40af", fontWeight: 600 }}>UPDATED</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#2563eb" }}>{importResults.updated}</div>
                </div>
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: 12, textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "#991b1b", fontWeight: 600 }}>FAILED</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#dc2626" }}>{importResults.failed}</div>
                </div>
              </div>

              {/* Errors list if any */}
              {importResults.errors.length > 0 && (
                <div style={{ maxHeight: 180, overflowY: "auto", background: "#fff1f2", border: "1px solid #fecaca", borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <strong style={{ color: "#991b1b", fontSize: 13 }}>Failed Records:</strong>
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12, color: "#b91c1c" }}>
                    {importResults.errors.map((err, idx) => (
                      <li key={idx}>
                        Row {err.RowIndex} ({err.CardNo || err.EmployeeName}): {err.Message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  className="btn btn-primary"
                  style={{ flex: 1, borderRadius: 8, fontWeight: 600, padding: "10px" }}
                  onClick={() => {
                    setShowResultModal(false);
                    resetImport();
                  }}
                >
                  OK, Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollImportExport;
